import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import IdentityCard from '../components/IdentityCard';

/* ─────────────────────────────────────────────
   SCROLL REVEAL, HIGHLIGHT, AND COUNT: same pattern as DIXTRAL and PunchPulse
───────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('an-revealed'); }),
      { threshold: 0.07 }
    );
    document.querySelectorAll('.an-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function useCoverFade(ref: React.RefObject<HTMLElement | null>) {
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const progress = Math.min(Math.max(-rect.top / (rect.height * 0.6), 0), 1);
      setOpacity(1 - progress);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref]);
  return opacity;
}

function Hl({ children, color = 'yellow' }: { children: React.ReactNode; color?: 'yellow' | 'blue' | 'pink' | 'teal' }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setTimeout(() => setActive(true), 180);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <span ref={ref} className={`an-hl an-hl-${color} ${active ? 'an-hl-active' : ''}`}>{children}</span>;
}

function CountStat({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const steps = 40; const duration = 900;
        const inc = target / steps; let cur = 0;
        const id = setInterval(() => {
          cur = Math.min(cur + inc, target);
          setVal(Math.round(cur));
          if (cur >= target) clearInterval(id);
        }, duration / steps);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return (
    <div ref={ref} style={{ fontSize: '3rem', fontWeight: 800, color: '#0d9488', lineHeight: 1 }}>
      {val}{suffix}
    </div>
  );
}

/* Wraps a label onto at most two lines so SVG text never overruns a fixed-width box. */
function wrapTwoLines(text: string, maxLen = 20): [string, string?] {
  if (text.length <= maxLen) return [text];
  let idx = text.lastIndexOf(' ', maxLen);
  if (idx === -1) idx = text.indexOf(' ', maxLen);
  if (idx === -1) return [text];
  return [text.slice(0, idx), text.slice(idx + 1)];
}

/* ─────────────────────────────────────────────
   SVG: DEVICE TRIAD (cover diagram)
   Three devices radiating from a central AI node.
───────────────────────────────────────────── */
function DeviceTriadDiagram() {
  return (
    <svg viewBox="0 0 600 380" style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label="Diagram showing an Apple Watch, an iPhone, and AirPods connected by lines to a central AI co-navigator node, each labeled with its role in the system.">
      <defs>
        <linearGradient id="an-tri-case" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#52525b" /><stop offset="1" stopColor="#18181b" />
        </linearGradient>
        <linearGradient id="an-tri-bud" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fafafa" /><stop offset="1" stopColor="#c7c9cc" />
        </linearGradient>
      </defs>
      <line x1="300" y1="195" x2="146" y2="98" stroke="#2dd4bf" strokeWidth="1.5" strokeDasharray="3 5" opacity="0.55" />
      <line x1="300" y1="195" x2="460" y2="92" stroke="#2dd4bf" strokeWidth="1.5" strokeDasharray="3 5" opacity="0.55" />
      <line x1="300" y1="195" x2="300" y2="318" stroke="#2dd4bf" strokeWidth="1.5" strokeDasharray="3 5" opacity="0.55" />
      <circle cx="300" cy="195" r="50" fill="#0f172a" stroke="#2dd4bf" strokeWidth="2" />
      <circle cx="300" cy="195" r="50" fill="none" stroke="#2dd4bf" strokeWidth="1" opacity="0.3">
        <animate attributeName="r" values="44;56;44" dur="3.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.45;0.05;0.45" dur="3.6s" repeatCount="indefinite" />
      </circle>
      <text x="300" y="189" textAnchor="middle" fill="#5eead4" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">PERSONAL</text>
      <text x="300" y="203" textAnchor="middle" fill="#5eead4" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">DIFFICULTY</text>
      <text x="300" y="217" textAnchor="middle" fill="#5eead4" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">MODEL</text>

      {/* Apple Watch, simplified case + crown + dial */}
      <g>
        <rect x="106" y="38" width="80" height="98" rx="22" fill="url(#an-tri-case)" stroke="#000" strokeOpacity="0.4" />
        <rect x="190" y="70" width="8" height="16" rx="3" fill="#3f3f46" />
        <rect x="190" y="92" width="6" height="20" rx="3" fill="#3f3f46" />
        <rect x="116" y="48" width="60" height="78" rx="14" fill="#05070a" />
        <circle cx="146" cy="87" r="22" fill="none" stroke="#1e293b" strokeWidth="2" />
        <g transform="rotate(-30 146 87)"><polygon points="146,68 141,86 151,86" fill="#2dd4bf" /></g>
        <text x="146" y="158" textAnchor="middle" fill="#e5e7eb" fontSize="13" fontWeight="700" fontFamily="Inter, sans-serif">Apple Watch</text>
        <text x="146" y="174" textAnchor="middle" fill="#9ca3af" fontSize="10.5" fontFamily="Inter, sans-serif">always-on direction, on the wrist</text>
      </g>

      {/* iPhone, simplified case + dynamic island + overlay arrow */}
      <g>
        <rect x="428" y="22" width="78" height="118" rx="20" fill="url(#an-tri-case)" stroke="#000" strokeOpacity="0.4" />
        <rect x="436" y="30" width="62" height="102" rx="13" fill="#05070a" />
        <rect x="457" y="38" width="20" height="7" rx="3.5" fill="#0a0a0a" />
        <g transform="rotate(20 467 90)"><polygon points="467,72 459,96 475,96" fill="#2dd4bf" /></g>
        <rect x="458" y="123" width="18" height="3" rx="1.5" fill="#3f3f46" />
        <text x="467" y="158" textAnchor="middle" fill="#e5e7eb" fontSize="13" fontWeight="700" fontFamily="Inter, sans-serif">iPhone</text>
        <text x="467" y="174" textAnchor="middle" fill="#9ca3af" fontSize="10.5" fontFamily="Inter, sans-serif">live overlay, the co-pilot's screen</text>
      </g>

      {/* AirPods, angled stem and bud pair */}
      <g>
        <g transform="rotate(-18 282 300)">
          <rect x="278" y="298" width="9" height="32" rx="4.5" fill="url(#an-tri-bud)" />
          <ellipse cx="282" cy="292" rx="13" ry="16" fill="url(#an-tri-bud)" />
          <circle cx="282" cy="288" r="4" fill="#9ca3af" />
        </g>
        <g transform="rotate(18 318 300)">
          <rect x="313" y="298" width="9" height="32" rx="4.5" fill="url(#an-tri-bud)" />
          <ellipse cx="318" cy="292" rx="13" ry="16" fill="url(#an-tri-bud)" />
          <circle cx="318" cy="288" r="4" fill="#9ca3af" />
        </g>
        <text x="300" y="350" textAnchor="middle" fill="#e5e7eb" fontSize="13" fontWeight="700" fontFamily="Inter, sans-serif">AirPods</text>
        <text x="300" y="366" textAnchor="middle" fill="#9ca3af" fontSize="10.5" fontFamily="Inter, sans-serif">spatial narration and the &ldquo;why&rdquo;</text>
      </g>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   SVG: EGOCENTRIC vs ALLOCENTRIC mini icons
───────────────────────────────────────────── */
function AllocentricIcon() {
  return (
    <svg viewBox="0 0 120 100" style={{ width: 110, height: 92 }} role="img" aria-label="A bird's-eye map with a north-up arrow, disconnected from the body's facing direction.">
      <rect x="8" y="8" width="104" height="84" rx="8" fill="#fff" stroke="#fecaca" strokeWidth="2" />
      <line x1="8" y1="40" x2="112" y2="40" stroke="#fecaca" strokeWidth="1.5" />
      <line x1="50" y1="8" x2="50" y2="92" stroke="#fecaca" strokeWidth="1.5" />
      <circle cx="80" cy="65" r="5" fill="#dc2626" />
      <polygon points="30,20 36,30 24,30" fill="#dc2626" transform="rotate(35 30 25)" />
      <text x="60" y="14" textAnchor="middle" fontSize="9" fill="#b91c1c" fontWeight="700">N ↑ (map-relative)</text>
    </svg>
  );
}
function EgocentricIcon() {
  return (
    <svg viewBox="0 0 120 100" style={{ width: 110, height: 92 }} role="img" aria-label="A first-person view with a single arrow pointing exactly where the body should turn next, always forward-relative.">
      <rect x="8" y="8" width="104" height="84" rx="8" fill="#fff" stroke="#86efac" strokeWidth="2" />
      <circle cx="60" cy="70" r="7" fill="#16a34a" />
      <polygon points="60,55 50,30 70,30" fill="#16a34a" />
      <text x="60" y="20" textAnchor="middle" fontSize="9" fill="#15803d" fontWeight="700">always &ldquo;forward&rdquo;</text>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   APPLE WATCH: shared chrome (case, digital crown,
   side button) plus three interchangeable screen faces.
   Proportions are modeled on the actual case geometry
   (rounded rectangle, crown at upper right, button below).
───────────────────────────────────────────── */
function WatchChrome({ children, size = 160 }: { children: React.ReactNode; size?: number }) {
  return (
    <svg viewBox="0 0 176 214" width={size} height={size * 1.215} role="img" aria-label="Apple Watch case with a digital crown and side button">
      <defs>
        <linearGradient id="an-watch-case" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#5a5a5e" />
          <stop offset="0.45" stopColor="#2c2c2e" />
          <stop offset="1" stopColor="#121214" />
        </linearGradient>
        <radialGradient id="an-watch-glass" cx="0.3" cy="0.2" r="0.9">
          <stop offset="0" stopColor="#1c2a33" />
          <stop offset="0.6" stopColor="#070a0d" />
          <stop offset="1" stopColor="#000" />
        </radialGradient>
      </defs>
      <rect x="8" y="8" width="148" height="196" rx="42" fill="url(#an-watch-case)" />
      <rect x="8" y="8" width="148" height="196" rx="42" fill="none" stroke="#000" strokeOpacity="0.5" strokeWidth="1.5" />
      <rect x="18" y="18" width="128" height="176" rx="32" fill="url(#an-watch-glass)" />
      <rect x="21" y="21" width="122" height="40" rx="20" fill="#fff" opacity="0.04" />
      {/* digital crown */}
      <rect x="154" y="78" width="16" height="28" rx="6" fill="#3a3a3c" stroke="#000" strokeOpacity="0.4" />
      <rect x="157" y="82" width="10" height="20" rx="4" fill="#5a5a5e" />
      {/* side button */}
      <rect x="156" y="122" width="11" height="32" rx="4.5" fill="#3a3a3c" stroke="#000" strokeOpacity="0.4" />
      {/* screen content, positioned within the 18..146 x 18..194 glass area */}
      <g transform="translate(22, 24)">{children}</g>
    </svg>
  );
}

/* Shared tone palette so the watch, phone, and AirPods escalate together as a scenario changes. */
type AnchorTone = 'normal' | 'urgent' | 'confirmed';
const TONE_COLOR: Record<AnchorTone, string> = { normal: '#2dd4bf', urgent: '#fb923c', confirmed: '#34d399' };

function WatchFaceVectorContent({ angle, urgent = false, confirmed = false }: { angle: number; urgent?: boolean; confirmed?: boolean }) {
  const tone: AnchorTone = confirmed ? 'confirmed' : urgent ? 'urgent' : 'normal';
  const c = TONE_COLOR[tone];
  const pulsing = tone !== 'normal';
  return (
    <>
      <text x="60" y="14" textAnchor="middle" fontSize="9" fontWeight="700" letterSpacing="0.5" fill="#5eead4" fontFamily="Inter, sans-serif">ANCHOR</text>
      <circle cx="60" cy="78" r="46" fill="none" stroke="#1e293b" strokeWidth="3" />
      <circle cx="60" cy="78" r="46" fill="none" stroke={c} strokeWidth="2.5" opacity="0.55">
        {pulsing && <animate attributeName="r" values="40;48;40" dur="1.1s" repeatCount="indefinite" />}
        {pulsing && <animate attributeName="opacity" values="0.85;0.1;0.85" dur="1.1s" repeatCount="indefinite" />}
      </circle>
      <circle cx="60" cy="78" r="3.5" fill="#5eead4" />
      <g transform={`rotate(${angle} 60 78)`} style={{ transition: 'transform 0.6s cubic-bezier(.22,1,.36,1)' }}>
        <polygon points="60,36 51,64 69,64" fill={c} />
        <rect x="56" y="64" width="8" height="20" fill={c} />
      </g>
      <text x="60" y="150" textAnchor="middle" fontSize="9.5" fill="#cbd5e1" fontWeight="600" fontFamily="Inter, sans-serif">always points true</text>
      <text x="60" y="163" textAnchor="middle" fontSize="8" fill="#64748b" fontFamily="Inter, sans-serif">no map, no rotation needed</text>
    </>
  );
}
function WatchFaceVector({ angle, urgent = false }: { angle: number; urgent?: boolean }) {
  return <WatchChrome><WatchFaceVectorContent angle={angle} urgent={urgent} /></WatchChrome>;
}

function WatchFaceEta({ ordinal = 'NOT THIS ONE', label = 'next door, right' }: { ordinal?: string; label?: string }) {
  const ordinalLines = wrapTwoLines(ordinal, 11);
  const labelLines = wrapTwoLines(label, 18);
  const ordY1 = ordinalLines[1] ? 56 : 66;
  const labelY = ordinalLines[1] ? 96 : 90;
  const iconY = labelLines[1] ? 128 : 118;
  return (
    <WatchChrome>
      <text x="60" y="14" textAnchor="middle" fontSize="9" fontWeight="700" letterSpacing="0.5" fill="#5eead4" fontFamily="Inter, sans-serif">HEADING SAYS</text>
      <text x="60" y={ordY1} textAnchor="middle" fontSize="20" fontWeight="800" fill="#f1f5f9" fontFamily="Inter, sans-serif">{ordinalLines[0]}</text>
      {ordinalLines[1] && <text x="60" y={ordY1 + 20} textAnchor="middle" fontSize="20" fontWeight="800" fill="#f1f5f9" fontFamily="Inter, sans-serif">{ordinalLines[1]}</text>}
      <text x="60" y={labelY} textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#2dd4bf" fontFamily="Inter, sans-serif">{labelLines[0]}</text>
      {labelLines[1] && <text x="60" y={labelY + 13} textAnchor="middle" fontSize="9.5" fontWeight="600" fill="#2dd4bf" fontFamily="Inter, sans-serif">{labelLines[1]}</text>}
      <g transform={`translate(60 ${iconY})`}>
        <circle r="14" fill="#0f172a" stroke="#1e293b" strokeWidth="2" />
        <polygon points="0,-8 -6,6 6,6" fill="#2dd4bf" transform="rotate(-35)" />
      </g>
      <text x="60" y="163" textAnchor="middle" fontSize="7.5" fill="#64748b" fontFamily="Inter, sans-serif">from gyro heading</text>
    </WatchChrome>
  );
}

function WatchFaceModel() {
  return (
    <WatchChrome>
      <text x="60" y="14" textAnchor="middle" fontSize="9" fontWeight="700" letterSpacing="0.5" fill="#5eead4" fontFamily="Inter, sans-serif">REMEMBERS</text>
      {[
        { t: 'Garage exit', s: '2x lead time' },
        { t: 'Bridge ramp', s: 'repeats cue' },
      ].map((r, i) => (
        <g key={r.t} transform={`translate(8 ${34 + i * 46})`}>
          <rect width="104" height="38" rx="10" fill="#0f1c24" stroke="#1e293b" strokeWidth="1.5" />
          <circle cx="20" cy="19" r="9" fill="#0d9488" opacity="0.25" />
          <circle cx="20" cy="19" r="3.5" fill="#5eead4" />
          <text x="36" y="16" fontSize="9.5" fontWeight="700" fill="#e2e8f0" fontFamily="Inter, sans-serif">{r.t}</text>
          <text x="36" y="28" fontSize="8" fill="#5eead4" fontFamily="Inter, sans-serif">{r.s}</text>
        </g>
      ))}
      <text x="60" y="163" textAnchor="middle" fontSize="8" fill="#64748b" fontFamily="Inter, sans-serif">editable on the phone</text>
    </WatchChrome>
  );
}

function WatchDial({ angle, urgent = false, confirmed = false, size = 130 }: { angle: number; urgent?: boolean; confirmed?: boolean; size?: number }) {
  return (
    <div style={{ display: 'inline-block' }}>
      <WatchChrome size={size}><WatchFaceVectorContent angle={angle} urgent={urgent} confirmed={confirmed} /></WatchChrome>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SVG: PHONE AR-LITE OVERLAY MOCKUP
───────────────────────────────────────────── */
function PhoneChrome({ children, screenFill = '#0a0f14' }: { children: React.ReactNode; screenFill?: string }) {
  return (
    <svg viewBox="0 0 232 470" style={{ width: '100%', maxWidth: 220, height: 'auto', display: 'block', margin: '0 auto' }} role="img" aria-label="iPhone case with a dynamic island and home indicator">
      <defs>
        <linearGradient id="an-phone-case" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#5a5a5e" /><stop offset="0.5" stopColor="#2c2c2e" /><stop offset="1" stopColor="#101012" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="220" height="458" rx="52" fill="url(#an-phone-case)" />
      <rect x="6" y="6" width="220" height="458" rx="52" fill="none" stroke="#000" strokeOpacity="0.5" strokeWidth="1.5" />
      <rect x="16" y="16" width="200" height="438" rx="44" fill={screenFill} />
      {/* volume buttons */}
      <rect x="2" y="128" width="4" height="34" rx="2" fill="#1f1f21" />
      <rect x="2" y="172" width="4" height="34" rx="2" fill="#1f1f21" />
      {/* side button */}
      <rect x="226" y="150" width="4" height="52" rx="2" fill="#1f1f21" />
      {/* dynamic island */}
      <rect x="86" y="32" width="60" height="20" rx="10" fill="#000" />
      <circle cx="132" cy="42" r="3.5" fill="#0a0a0a" stroke="#1a1a1a" strokeWidth="0.5" />
      <g transform="translate(16,16)">{children}</g>
      <rect x="96" y="438" width="40" height="4" rx="2" fill="#cbd5e1" opacity="0.7" />
    </svg>
  );
}

function Building3D({ x, y, w, h, side, accent = false }: { x: number; y: number; w: number; h: number; side: 'left' | 'right'; accent?: boolean }) {
  const dx = side === 'left' ? 7 : -7;
  const dy = -7;
  const front = accent ? '#1f3a3a' : '#1a2228';
  const sideFill = accent ? '#173533' : '#11161b';
  const roof = accent ? '#234543' : '#1c252b';
  const sidePoints = side === 'left'
    ? `${x + w},${y} ${x + w + dx},${y + dy} ${x + w + dx},${y + h + dy} ${x + w},${y + h}`
    : `${x},${y} ${x + dx},${y + dy} ${x + dx},${y + h + dy} ${x},${y + h}`;
  return (
    <g>
      <polygon points={`${x},${y} ${x + w},${y} ${x + w + dx},${y + dy} ${x + dx},${y + dy}`} fill={roof} stroke="#000" strokeOpacity="0.4" strokeWidth="0.5" />
      <polygon points={sidePoints} fill={sideFill} stroke="#000" strokeOpacity="0.4" strokeWidth="0.5" />
      <rect x={x} y={y} width={w} height={h} fill={front} stroke="#000" strokeOpacity="0.4" strokeWidth="0.5" />
      {Array.from({ length: Math.max(1, Math.floor(h / 24)) }).map((_, row) => (
        <rect key={row} x={x + w * 0.2} y={y + 9 + row * 24} width={w * 0.6} height="5" fill={accent ? '#5eead4' : '#2b3a47'} opacity={accent ? 0.75 : 0.55} />
      ))}
      {accent && <circle cx={x + w / 2} cy={y - 9} r="4" fill="#2dd4bf" />}
    </g>
  );
}

/* Four scenario scenes so the overlay's background, not just the arrow, changes with the situation. */
function PhoneSceneStreet({ c }: { c: string }) {
  return (
    <>
      <line x1="36" y1="400" x2="70" y2="95" stroke="#22303b" strokeWidth="2" />
      <line x1="164" y1="400" x2="130" y2="95" stroke="#22303b" strokeWidth="2" />
      <line x1="50" y1="400" x2="76" y2="120" stroke="#1a242c" strokeWidth="1" strokeDasharray="3 5" />
      <Building3D x={58} y={150} w={14} h={55} side="left" />
      <Building3D x={128} y={150} w={14} h={55} side="right" />
      <Building3D x={26} y={210} w={28} h={150} side="left" accent />
      <Building3D x={146} y={210} w={28} h={150} side="right" />
      <Building3D x={2} y={262} w={28} h={138} side="left" />
      <Building3D x={170} y={262} w={28} h={138} side="right" />
    </>
  );
}
function PhoneSceneDoors({ c }: { c: string }) {
  return (
    <>
      <rect x="14" y="64" width="172" height="320" fill="#141a1f" stroke="#22303b" strokeWidth="2" />
      {[0, 1, 2].map(i => <rect key={i} x={28 + i * 52} y="94" width="34" height="40" fill="#1c2730" stroke="#2b3a47" strokeWidth="1" />)}
      <rect x="36" y="290" width="38" height="92" rx="3" fill="#1c2730" stroke="#3f4f5c" strokeWidth="1.5" />
      <text x="55" y="318" textAnchor="middle" fontSize="14" fill="#52646f" fontFamily="Inter, sans-serif">✕</text>
      <g>
        <rect x="124" y="290" width="38" height="92" rx="3" fill="#1c2730" stroke={c} strokeWidth="3" />
        <circle cx="143" cy="278" r="4.5" fill={c} />
      </g>
    </>
  );
}
function PhoneSceneCrosswalk({ c }: { c: string }) {
  return (
    <>
      <rect x="0" y="64" width="200" height="320" fill="#0e1318" />
      <rect x="0" y="160" width="200" height="46" fill="#161c22" />
      {[0, 1, 2, 3, 4].map(i => <rect key={i} x={20 + i * 34} y="166" width="14" height="34" fill="#2b3a47" opacity="0.7" />)}
      <rect x="0" y="300" width="200" height="46" fill="#161c22" />
      {[0, 1, 2, 3, 4].map(i => <rect key={i} x={20 + i * 34} y="306" width="14" height="34" fill={c} opacity="0.85" />)}
      <circle cx="34" cy="292" r="4.5" fill={c} />
    </>
  );
}
function PhoneSceneGarage({ c }: { c: string }) {
  return (
    <>
      <rect x="0" y="64" width="200" height="320" fill="#10151a" />
      <rect x="74" y="64" width="26" height="320" fill="#1a2228" stroke="#2b3a47" strokeWidth="1.5" />
      <text x="87" y="200" textAnchor="middle" fontSize="8" fill="#52646f" fontFamily="Inter, sans-serif" transform="rotate(-90 87 200)">column</text>
      <rect x="20" y="356" width="46" height="16" rx="3" fill="#1c2730" stroke="#2b3a47" strokeWidth="1" />
      <g>
        <rect x="110" y="350" width="62" height="18" rx="3" fill="#1c2730" stroke={c} strokeWidth="3" />
        <circle cx="141" cy="338" r="4.5" fill={c} />
      </g>
    </>
  );
}
function PhoneSceneLot({ c }: { c: string }) {
  return (
    <>
      <rect x="0" y="64" width="200" height="320" fill="#0e1318" />
      {[0, 1, 2].map(row => (
        <g key={row}>
          {[0, 1, 2, 3].map(i => (
            <rect key={i} x={16 + i * 44} y={100 + row * 96} width="32" height="56" rx="7"
              fill={row === 2 ? c : '#1c2730'} opacity={row === 2 ? 0.88 : 1} stroke="#2b3a47" strokeWidth="1" />
          ))}
          {row === 2 && <circle cx="32" cy={100 + row * 96 - 12} r="4.5" fill={c} />}
        </g>
      ))}
    </>
  );
}

function PhoneScreenAR({ angle, label, scene = 'street', urgent = false, confirmed = false }: { angle: number; label: string; scene?: 'street' | 'doors' | 'crosswalk' | 'garage' | 'lot'; urgent?: boolean; confirmed?: boolean }) {
  const tone: AnchorTone = confirmed ? 'confirmed' : urgent ? 'urgent' : 'normal';
  const c = TONE_COLOR[tone];
  return (
    <PhoneChrome>
      <defs>
        <linearGradient id="an-ar-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0c1a24" /><stop offset="1" stopColor="#03070a" />
        </linearGradient>
      </defs>
      <rect width="200" height="438" rx="44" fill="url(#an-ar-sky)" />
      <text x="100" y="20" textAnchor="middle" fontSize="9" fill="#e2e8f0" fontFamily="Inter, sans-serif">9:41</text>
      <g transform="translate(160,12)" opacity="0.85">
        <rect width="18" height="9" rx="2" fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <rect x="1" y="1" width="13" height="7" fill="#e2e8f0" />
      </g>
      <rect x="20" y="34" width="56" height="18" rx="9" fill="rgba(13,148,136,0.18)" stroke={c} strokeWidth="1" />
      <circle cx="30" cy="43" r="3.5" fill={c} />
      <text x="38" y="46" fontSize="8" fontWeight="700" fill={c} fontFamily="Inter, sans-serif">ANCHOR</text>

      {/* the scenario itself, drawn as simplified 3D line art, never a photo */}
      {scene === 'street' && <PhoneSceneStreet c={c} />}
      {scene === 'doors' && <PhoneSceneDoors c={c} />}
      {scene === 'crosswalk' && <PhoneSceneCrosswalk c={c} />}
      {scene === 'garage' && <PhoneSceneGarage c={c} />}
      {scene === 'lot' && <PhoneSceneLot c={c} />}

      <g transform={`rotate(${angle} 100 250)`} style={{ transition: 'transform 0.5s ease' }}>
        <polygon points="100,196 84,242 116,242" fill={c} />
        <rect x="91" y="242" width="18" height="32" fill={c} />
      </g>
      {(() => {
        const lines = wrapTwoLines(label, 19);
        const boxH = lines.length > 1 ? 58 : 44;
        return (
          <>
            <rect x="16" y={392 - boxH} width="168" height={boxH} rx="10" fill={c} fillOpacity="0.95" />
            <text x="100" y={lines.length > 1 ? 392 - boxH + 24 : 392 - boxH + 27} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">{lines[0]}</text>
            {lines[1] && <text x="100" y={392 - boxH + 42} textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">{lines[1]}</text>}
          </>
        );
      })()}
    </PhoneChrome>
  );
}

function PhoneScreenLiveActivity({ label = 'Not this exit, the next' }: { label?: string }) {
  const lines = wrapTwoLines(label, 16);
  const pillH = lines[1] ? 44 : 32;
  return (
    <PhoneChrome screenFill="#000">
      <defs>
        <radialGradient id="an-wall-1" cx="0.3" cy="0.2" r="0.9">
          <stop offset="0" stopColor="#1e3a3a" /><stop offset="1" stopColor="#06100f" />
        </radialGradient>
      </defs>
      <rect width="200" height="438" rx="44" fill="url(#an-wall-1)" />
      <text x="100" y="80" textAnchor="middle" fontSize="40" fontWeight="300" fill="#e2e8f0" fontFamily="Inter, sans-serif">9:41</text>
      <text x="100" y="104" textAnchor="middle" fontSize="11" fill="#94a3b8" fontFamily="Inter, sans-serif">Tuesday, 12 May</text>
      <rect x="24" y="26" width="152" height={pillH} rx="16" fill="#000" />
      <g transform="rotate(20 46 42)"><polygon points="46,34 40,48 52,48" fill="#2dd4bf" /></g>
      <text x="58" y={lines[1] ? 40 : 46} fontSize="9" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">{lines[0]}</text>
      {lines[1] && <text x="58" y="54" fontSize="9" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">{lines[1]}</text>}
      <text x="100" y="420" textAnchor="middle" fontSize="8.5" fill="#64748b" fontFamily="Inter, sans-serif">live, glanceable, nothing to unlock</text>
    </PhoneChrome>
  );
}

function PhoneScreenModel() {
  const rows = [
    { t: 'Westgate garage exit', s: 'Warns 2x earlier, repeats once' },
    { t: 'Route 9 / Bridge St interchange', s: 'Names the landmark first' },
    { t: 'Lakeside Mall lower exit', s: 'Standard timing, not flagged' },
  ];
  return (
    <PhoneChrome screenFill="#0b0e12">
      <text x="100" y="18" textAnchor="middle" fontSize="9" fill="#e2e8f0" fontFamily="Inter, sans-serif">9:41</text>
      <text x="16" y="42" fontSize="13.5" fontWeight="800" fill="#f1f5f9" fontFamily="Inter, sans-serif">Difficulty Model</text>
      <text x="16" y="58" fontSize="8.5" fill="#64748b" fontFamily="Inter, sans-serif">what Anchor has learned, editable</text>
      {rows.map((r, i) => {
        const titleLines = wrapTwoLines(r.t, 17);
        const rowH = titleLines.length > 1 ? 60 : 48;
        let cursorY = 74;
        for (let j = 0; j < i; j++) cursorY += (wrapTwoLines(rows[j].t, 17).length > 1 ? 60 : 48) + 10;
        return (
          <g key={r.t} transform={`translate(16 ${cursorY})`}>
            <rect width="168" height={rowH} rx="12" fill="#141a20" stroke="#1f2937" strokeWidth="1" />
            <text x="12" y="18" fontSize="9.5" fontWeight="700" fill="#e2e8f0" fontFamily="Inter, sans-serif">{titleLines[0]}</text>
            {titleLines[1] && <text x="12" y="31" fontSize="9.5" fontWeight="700" fill="#e2e8f0" fontFamily="Inter, sans-serif">{titleLines[1]}</text>}
            <text x="12" y={rowH - 8} fontSize="8.5" fill="#5eead4" fontFamily="Inter, sans-serif">{r.s}</text>
            <rect x="138" y="12" width="20" height="11" rx="5.5" fill={i < 2 ? '#0d9488' : '#334155'} />
            <circle cx={i < 2 ? 153 : 144} cy="17.5" r="4.5" fill="#fff" />
          </g>
        );
      })}
    </PhoneChrome>
  );
}

function PhoneOverlay({ angle, label, scene = 'street', urgent = false, confirmed = false }: { angle: number; label: string; scene?: 'street' | 'doors' | 'crosswalk' | 'garage' | 'lot'; urgent?: boolean; confirmed?: boolean }) {
  return <PhoneScreenAR angle={angle} label={label} scene={scene} urgent={urgent} confirmed={confirmed} />;
}

/* ─────────────────────────────────────────────
   APPLE CARPLAY: a landscape dashboard screen,
   used while driving, drawing the actual ramp
   geometry of a butterfly interchange instead of
   a generic turn arrow, since that geometry is
   the entire source of confusion in Problem 02.
───────────────────────────────────────────── */
function CarPlayDashboard() {
  return (
    <svg viewBox="0 0 380 230" style={{ width: '100%', maxWidth: 420, height: 'auto', display: 'block', margin: '0 auto' }} role="img"
      aria-label="Apple CarPlay dashboard screen showing the ramp geometry of a butterfly interchange with bridge decks, and an AI overlay arrow telling the driver to stay on the lower right ramp, with the reasoning written underneath.">
      <defs>
        <linearGradient id="an-carplay-bezel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3a3a3c" /><stop offset="1" stopColor="#161618" />
        </linearGradient>
        <linearGradient id="an-carplay-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0b1620" /><stop offset="1" stopColor="#02060a" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="380" height="230" rx="18" fill="url(#an-carplay-bezel)" />
      <rect x="8" y="8" width="364" height="214" rx="13" fill="url(#an-carplay-sky)" />

      {/* status row */}
      <text x="22" y="28" fontSize="11" fill="#e2e8f0" fontFamily="Inter, sans-serif">9:41</text>
      <text x="190" y="28" textAnchor="middle" fontSize="10" fontWeight="700" letterSpacing="0.5" fill="#94a3b8" fontFamily="Inter, sans-serif">CARPLAY · ANCHOR</text>
      <circle cx="350" cy="24" r="5" fill="#2dd4bf" />

      {/* butterfly interchange: two looping ramps crossing over a bridge deck, drawn as the actual confusing geometry */}
      <g opacity="0.95">
        <path d="M30,170 C90,170 110,60 190,60 C270,60 290,170 350,170" fill="none" stroke="#3f3f46" strokeWidth="7" />
        <path d="M30,60 C90,60 110,170 190,170 C270,170 290,60 350,60" fill="none" stroke="#2b2b2e" strokeWidth="7" />
        <rect x="150" y="96" width="80" height="14" rx="3" fill="#26262a" stroke="#3f3f46" strokeWidth="1.5" />
        <text x="190" y="92" textAnchor="middle" fontSize="8" fill="#52525b" fontFamily="Inter, sans-serif">bridge deck</text>
      </g>

      {/* AI overlay: which ramp, drawn directly onto the correct path, plus the reasoning */}
      <g>
        <path d="M190,170 C150,170 130,130 130,103" fill="none" stroke="#2dd4bf" strokeWidth="5" strokeLinecap="round" opacity="0.95" />
        <polygon points="130,98 122,114 138,114" fill="#2dd4bf" />
      </g>
      <rect x="46" y="190" width="288" height="30" rx="9" fill="rgba(13,148,136,0.95)" />
      <text x="190" y="206" textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">Stay right onto the lower ramp</text>
      <text x="190" y="217" textAnchor="middle" fontSize="8" fill="#d1fae5" fontFamily="Inter, sans-serif">the upper ramp loops back the way you came</text>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   AIRPODS: realistic stem and bud illustration,
   reused across the narration, query, and mode cards.
───────────────────────────────────────────── */
function AirPodsIcon({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 120 84" aria-hidden="true">
      <defs>
        <linearGradient id="an-bud-gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" /><stop offset="1" stopColor="#c4c8cc" />
        </linearGradient>
      </defs>
      <g transform="rotate(-16 36 40)">
        <rect x="32" y="44" width="9" height="32" rx="4.5" fill="url(#an-bud-gloss)" />
        <ellipse cx="36" cy="36" rx="14" ry="17" fill="url(#an-bud-gloss)" />
        <circle cx="36" cy="32" r="4.5" fill="#9aa0a6" />
        <ellipse cx="31" cy="28" rx="4" ry="5.5" fill="#fff" opacity="0.6" />
      </g>
      <g transform="rotate(16 84 40)">
        <rect x="79" y="44" width="9" height="32" rx="4.5" fill="url(#an-bud-gloss)" />
        <ellipse cx="84" cy="36" rx="14" ry="17" fill="url(#an-bud-gloss)" />
        <circle cx="84" cy="32" r="4.5" fill="#9aa0a6" />
        <ellipse cx="79" cy="28" rx="4" ry="5.5" fill="#fff" opacity="0.6" />
      </g>
    </svg>
  );
}

function AirpodsBubble({ text, why, side = 'center', tone = 'normal' }: { text: string; why?: string | null; side?: 'left' | 'right' | 'center'; tone?: AnchorTone }) {
  const c = TONE_COLOR[tone];
  return (
    <div style={{ background: '#0f172a', borderRadius: 14, padding: '1.1rem 1.2rem', color: '#e2e8f0', border: `1.5px solid ${tone === 'normal' ? '#1e293b' : c}` }}>
      <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.6rem' }}>
        <AirPodsIcon size={40} />
        <div style={{ fontSize: '0.72rem', color: c, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {side === 'left' ? 'Audio panned left' : side === 'right' ? 'Audio panned right' : 'Audio centered'}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.55 }}>&ldquo;{text}&rdquo;</p>
      {why && <p style={{ margin: '0.6rem 0 0', fontSize: '0.82rem', color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.5 }}>{why}</p>}
    </div>
  );
}

function AirPodsQueryCard() {
  return (
    <div style={{ background: '#0f172a', borderRadius: 14, padding: '1.1rem 1.2rem', color: '#e2e8f0' }}>
      <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.7rem' }}>
        <AirPodsIcon size={40} />
        <div style={{ fontSize: '0.72rem', color: '#5eead4', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Double tap the stem</div>
      </div>
      <div style={{ background: '#1e293b', borderRadius: 10, padding: '0.6rem 0.8rem', marginBottom: '0.5rem' }}>
        <p style={{ margin: 0, fontSize: '0.84rem', color: '#cbd5e1' }}>&ldquo;Anchor, where is my car from here?&rdquo;</p>
      </div>
      <div style={{ background: 'rgba(13,148,136,0.16)', borderRadius: 10, padding: '0.6rem 0.8rem' }}>
        <p style={{ margin: 0, fontSize: '0.84rem', color: '#5eead4', fontWeight: 600 }}>&ldquo;Behind you, about forty feet, past the pillar on your left.&rdquo;</p>
      </div>
    </div>
  );
}

function AirPodsModeCard() {
  return (
    <div style={{ background: '#0f172a', borderRadius: 14, padding: '1.1rem 1.2rem', color: '#e2e8f0' }}>
      <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', marginBottom: '0.7rem' }}>
        <AirPodsIcon size={40} />
        <div style={{ fontSize: '0.72rem', color: '#5eead4', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Hearing mode, set by context</div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <div style={{ flex: 1, background: 'rgba(13,148,136,0.18)', border: '1.5px solid #0d9488', borderRadius: 10, padding: '0.5rem 0.6rem', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#5eead4' }}>Transparency</div>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.15rem' }}>on while walking or crossing streets</div>
        </div>
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 10, padding: '0.5rem 0.6rem', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#94a3b8' }}>Noise Cancellation</div>
          <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.15rem' }}>only once you are parked and stationary</div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SVG: LEAD TIME COMPARISON CHART
───────────────────────────────────────────── */
function LeadTimeChart() {
  return (
    <svg viewBox="0 0 560 160" style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label="Bar chart comparing a standard navigation alert that arrives at the decision point itself, against an adaptive alert that arrives two decision points earlier and repeats, counted in landmarks passed, not distance.">
      <text x="0" y="22" fontSize="12.5" fontWeight="700" fill="#1d1d1f" fontFamily="Inter, sans-serif">Standard turn-by-turn alert</text>
      <rect x="0" y="32" width="560" height="22" rx="6" fill="#f1f5f9" />
      <rect x="430" y="32" width="130" height="22" rx="6" fill="#fca5a5" />
      <text x="438" y="48" fontSize="11" fill="#7f1d1d" fontWeight="700" fontFamily="Inter, sans-serif">one alert, at the ramp itself</text>

      <text x="0" y="92" fontSize="12.5" fontWeight="700" fill="#1d1d1f" fontFamily="Inter, sans-serif">Anchor, after it has learned this exit</text>
      <rect x="0" y="102" width="560" height="22" rx="6" fill="#f1f5f9" />
      <rect x="210" y="102" width="350" height="22" rx="6" fill="#5eead4" />
      <rect x="430" y="102" width="130" height="22" rx="6" fill="#0d9488" />
      <text x="218" y="118" fontSize="11" fill="#0f766e" fontWeight="700" fontFamily="Inter, sans-serif">heads-up, two ramps earlier</text>
      <text x="438" y="118" fontSize="11" fill="#fff" fontWeight="700" fontFamily="Inter, sans-serif">repeats at this one</text>
    </svg>
  );
}

/* ─────────────────────────────────────────────
   SVG: SYSTEM LOOP (architecture)
───────────────────────────────────────────── */
function SystemLoopDiagram() {
  const nodes = [
    { x: 90, y: 60, label: 'Sensors', sub: 'GPS, compass, gyro, camera' },
    { x: 300, y: 60, label: 'Personal Difficulty Model', sub: 'remembers where you get lost' },
    { x: 510, y: 60, label: '3 Channels', sub: 'watch, phone, airpods' },
    { x: 510, y: 200, label: 'Your action', sub: 'turn, hesitate, miss it, correct' },
    { x: 90, y: 200, label: 'Updated model', sub: 'this point gets more lead time' },
  ];
  return (
    <svg viewBox="0 0 620 260" style={{ width: '100%', height: 'auto', display: 'block' }} role="img"
      aria-label="Loop diagram: sensors feed a personal difficulty model, which drives three output channels, which inform the user's action, which updates the model for next time.">
      <defs>
        <marker id="an-loop-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
        </marker>
      </defs>
      <line x1="178" y1="60" x2="212" y2="60" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#an-loop-arrow)" />
      <line x1="388" y1="60" x2="422" y2="60" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#an-loop-arrow)" />
      <line x1="510" y1="101" x2="510" y2="159" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#an-loop-arrow)" />
      <line x1="422" y1="200" x2="178" y2="200" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#an-loop-arrow)" />
      <line x1="90" y1="159" x2="90" y2="101" stroke="#94a3b8" strokeWidth="2" markerEnd="url(#an-loop-arrow)" />
      {nodes.map(n => {
        const subLines = wrapTwoLines(n.sub, 22);
        return (
          <g key={n.label}>
            <rect x={n.x - 84} y={n.y - 39} width="168" height="78" rx="12" fill="#f9f9fb" stroke="#0d9488" strokeWidth="1.5" />
            <text x={n.x} y={n.y - 14} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1d1d1f" fontFamily="Inter, sans-serif">{n.label}</text>
            <text x={n.x} y={n.y + 6} textAnchor="middle" fontSize="8.5" fill="#666" fontFamily="Inter, sans-serif">{subLines[0]}</text>
            {subLines[1] && <text x={n.x} y={n.y + 19} textAnchor="middle" fontSize="8.5" fill="#666" fontFamily="Inter, sans-serif">{subLines[1]}</text>}
          </g>
        );
      })}
    </svg>
  );
}

/* ─────────────────────────────────────────────
   SMART GLASSES: speculative design storyboard.
   Three frames of the same approach to a doorway,
   at decreasing distance, showing how the overlay
   would escalate. This is a design fiction, drawn
   as line art, never presented as a product photo.
───────────────────────────────────────────── */
function GlassesFrame({ stage, caption }: { stage: 'far' | 'mid' | 'near'; caption: string }) {
  const lensLeftX = 76, lensRightX = 184, lensY = 102, rx = 56, ry = 78;
  return (
    <div style={{ flex: '1 1 200px' }}>
      <svg viewBox="0 0 260 200" style={{ width: '100%', height: 'auto', display: 'block', background: '#0a0a0a', borderRadius: 14 }} role="img"
        aria-label={`Speculative storyboard frame, ${stage} approach to a doorway, AI overlay rendered inside the lens showing: ${caption}`}>
        <defs>
          <radialGradient id={`an-lenstint-${stage}`} cx="0.4" cy="0.3" r="0.9">
            <stop offset="0" stopColor="#16313a" stopOpacity="0.55" /><stop offset="1" stopColor="#020303" stopOpacity="0.9" />
          </radialGradient>
          <linearGradient id={`an-frame-${stage}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e8e8ea" /><stop offset="1" stopColor="#a3a3a8" />
          </linearGradient>
          <clipPath id={`an-lensclip-${stage}`}>
            <ellipse cx={lensLeftX} cy={lensY} rx={rx - 4} ry={ry - 4} />
            <ellipse cx={lensRightX} cy={lensY} rx={rx - 4} ry={ry - 4} />
          </clipPath>
        </defs>

        {/* everything below is clipped so it only ever renders inside the two lens shapes:
            the world (buildings, street) sits behind, the AI overlay is painted on top of it,
            both visible only through the glasses, never outside the frame */}
        <g clipPath={`url(#an-lensclip-${stage})`}>
          <rect width="260" height="200" fill="#0d1418" />
          {/* the world: a street with extruded buildings, exactly like the phone's street scene, seen through the lens */}
          <g opacity="0.95">
            <line x1="14" y1="190" x2="92" y2="56" stroke="#2b3a47" strokeWidth="1.5" />
            <line x1="246" y1="190" x2="168" y2="56" stroke="#2b3a47" strokeWidth="1.5" />
            <Building3D x={8} y={66} w={24} h={108} side="left" />
            <Building3D x={222} y={66} w={24} h={108} side="right" />
            <Building3D x={42} y={104} w={32} h={78} side="left" accent />
            <Building3D x={180} y={108} w={30} h={72} side="right" />
          </g>
          {/* the AI overlay, escalating with proximity, painted onto the lens above the world */}
          {stage === 'far' && (
            <g>
              <circle cx="130" cy="70" r="5" fill="#2dd4bf" opacity="0.9" />
              <circle cx="130" cy="70" r="5" fill="none" stroke="#2dd4bf" strokeWidth="1.5">
                <animate attributeName="r" values="5;15;5" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.75;0;0.75" dur="2s" repeatCount="indefinite" />
              </circle>
            </g>
          )}
          {stage === 'mid' && (
            <g>
              <polygon points="58,96 47,122 69,122" fill="#2dd4bf" />
              <rect x="36" y="150" width="116" height="22" rx="9" fill="rgba(13,148,136,0.94)" />
              <text x="94" y="166" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">not this building</text>
            </g>
          )}
          {stage === 'near' && (
            <g>
              <rect x="38" y="100" width="40" height="86" fill="none" stroke="#2dd4bf" strokeWidth="2.5" rx="6" opacity="0.95" />
              <circle cx="58" cy="92" r="11" fill="#0d9488" />
              <path d="M53,92 l4,5 l8,-9" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <rect x="20" y="160" width="148" height="22" rx="9" fill="rgba(13,148,136,0.96)" />
              <text x="94" y="175" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fff" fontFamily="Inter, sans-serif">this one, confirmed</text>
            </g>
          )}
          {/* a faint glass tint over the whole scene so it reads as a lens, not a bare screen */}
          <rect width="260" height="200" fill={`url(#an-lenstint-${stage})`} opacity="0.35" />
        </g>

        {/* frame drawn last, stroke only, never covering the overlay */}
        <ellipse cx={lensLeftX} cy={lensY} rx={rx} ry={ry} fill="none" stroke={`url(#an-frame-${stage})`} strokeWidth="5" />
        <ellipse cx={lensRightX} cy={lensY} rx={rx} ry={ry} fill="none" stroke={`url(#an-frame-${stage})`} strokeWidth="5" />
        <rect x="122" y="96" width="16" height="6" fill={`url(#an-frame-${stage})`} />
        <line x1="20" y1="86" x2="2" y2="70" stroke={`url(#an-frame-${stage})`} strokeWidth="5" strokeLinecap="round" />
        <line x1="240" y1="86" x2="258" y2="70" stroke={`url(#an-frame-${stage})`} strokeWidth="5" strokeLinecap="round" />
      </svg>
      <p style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', marginTop: '0.6rem' }}>{caption}</p>
    </div>
  );
}

function GlassesConceptSketch() {
  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <GlassesFrame stage="far" caption="approaching: a quiet point of light, nothing demanding attention yet" />
        <GlassesFrame stage="mid" caption="not this doorway: the arrow appears, matched to the body's own heading, not a distance" />
        <GlassesFrame stage="near" caption="this one: the doorway itself is outlined and confirmed, counted, not measured" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INTERACTIVE PROTOTYPE
   A real, working simulation of the watch + phone + AirPods
   loop, including the adaptive "learns the hard exit" moment.
   No live GPS or maps. This is explicitly a simulation.
───────────────────────────────────────────── */
type AnchorStep = {
  title: string;
  angle: number;
  watchLabel: string;
  phoneLabel: string;
  narration: string;
  why: string | null;
  side: 'left' | 'right' | 'center';
  scene: 'doors' | 'crosswalk' | 'garage' | 'lot';
  isHardPoint?: boolean;
};

const ANCHOR_STEPS: AnchorStep[] = [
  { title: 'Leaving the building', angle: 55, watchLabel: 'Not this door', phoneLabel: 'Not this one. The next door, on your right.', narration: 'Your heading is drifting toward the near door. Not that one, the next door.', why: 'The gyroscope caught your body turning toward the wrong door before you reached it, so the correction came before the mistake, not after.', side: 'right', scene: 'doors' },
  { title: 'Crossing the plaza', angle: 0, watchLabel: 'Straight ahead', phoneLabel: 'Not this crossing. The one after it.', narration: 'Straight across the plaza, past the first crossing. Take the second one.', why: null, side: 'center', scene: 'crosswalk' },
  { title: 'The parking garage exit', angle: -78, watchLabel: 'Not this ramp', phoneLabel: 'Not this ramp. Two ramps after this one.', narration: 'Not this ramp. Two ramps after this one.', why: 'This is the ramp you have missed twice before. The sign is hidden behind a support column, so counting ramps matters more than reading one.', side: 'left', scene: 'garage', isHardPoint: true },
  { title: 'Finding your car', angle: 82, watchLabel: 'Not this row', phoneLabel: 'Not this row. Two rows further, on your right.', narration: 'Not this row. Two rows further, on your right.', why: null, side: 'right', scene: 'lot' },
];

function AnchorPrototype() {
  const [stepIndex, setStepIndex] = useState(0);
  const [hardLearned, setHardLearned] = useState(false);
  const [missed, setMissed] = useState(false);

  const step = ANCHOR_STEPS[stepIndex];
  const onHardStep = !!step.isHardPoint;
  const showColdOutcome = onHardStep && !hardLearned && !missed;
  const showLearnedOutcome = onHardStep && hardLearned;

  const next = () => {
    if (onHardStep && !hardLearned) { setMissed(true); return; }
    setStepIndex(i => Math.min(i + 1, ANCHOR_STEPS.length - 1));
  };
  const reset = () => { setStepIndex(0); setHardLearned(false); setMissed(false); };

  return (
    <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #e5e7eb', padding: '1.6rem', boxShadow: '0 4px 30px rgba(0,0,0,0.06)' }}>
      {/* Stepper */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.4rem', flexWrap: 'wrap' }}>
        {ANCHOR_STEPS.map((s, i) => (
          <div key={s.title} style={{
            flex: '1 1 120px', textAlign: 'center', padding: '0.5rem 0.4rem', borderRadius: 8, fontSize: '0.75rem', fontWeight: 700,
            background: i === stepIndex ? '#0d9488' : '#f1f5f9', color: i === stepIndex ? '#fff' : '#64748b',
          }}>
            {i + 1}. {s.title}
          </div>
        ))}
      </div>

      {/* Hard-point banners */}
      {missed && !hardLearned && (
        <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.2rem' }}>
          <div style={{ fontWeight: 800, color: '#b91c1c', fontSize: '0.92rem', marginBottom: '0.3rem' }}>Missed it. Recalculating.</div>
          <p style={{ margin: '0 0 0.8rem', color: '#7f1d1d', fontSize: '0.86rem', lineHeight: 1.55 }}>
            The cue fired at the ramp itself, the way a standard turn-by-turn alert does. That is too late for this exit. By the time the column blocks the sign, the decision window is already closing.
          </p>
          <button onClick={() => setHardLearned(true)}
            style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            See what Anchor does differently next time &rarr;
          </button>
        </div>
      )}
      {showLearnedOutcome && (
        <div style={{ background: '#f0fdfa', border: '1.5px solid #5eead4', borderRadius: 12, padding: '1rem 1.2rem', marginBottom: '1.2rem' }}>
          <div style={{ fontWeight: 800, color: '#0f766e', fontSize: '0.92rem', marginBottom: '0.3rem' }}>Anchor remembers this exit.</div>
          <p style={{ margin: 0, color: '#115e59', fontSize: '0.86rem', lineHeight: 1.55 }}>
            The personal difficulty model logged the missed exit. The next pass through this spot, the warning starts two ramps earlier instead of one, names the landmark before the column hides it, and repeats once on approach. Nothing about the route changed. Only how early, and in how many counted steps, this particular decision point is flagged, for this particular person.
          </p>
        </div>
      )}

      {/* Three channels, all three escalate together: teal normal, orange when a known-hard point is approaching cold, green once Anchor has learned it */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Watch</div>
          <WatchDial angle={step.angle} urgent={showColdOutcome} confirmed={showLearnedOutcome} />
          <div style={{ fontSize: '0.86rem', fontWeight: 700, marginTop: '0.5rem', color: '#1d1d1f' }}>{step.watchLabel}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', textAlign: 'center' }}>Phone</div>
          <PhoneOverlay angle={step.angle} label={step.phoneLabel} scene={step.scene} urgent={showColdOutcome} confirmed={showLearnedOutcome} />
        </div>
        <div>
          <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', textAlign: 'center' }}>AirPods</div>
          <AirpodsBubble text={step.narration} why={step.why} side={step.side} tone={showLearnedOutcome ? 'confirmed' : showColdOutcome ? 'urgent' : 'normal'} />
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '0.7rem', marginTop: '1.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {stepIndex < ANCHOR_STEPS.length - 1 && !(onHardStep && missed && !hardLearned) && (
          <button onClick={next}
            style={{ background: '#0d9488', color: '#fff', border: 'none', borderRadius: 8, padding: '0.6rem 1.4rem', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
            {showLearnedOutcome ? 'Continue to next step →' : 'Next step →'}
          </button>
        )}
        <button onClick={reset}
          style={{ background: 'none', border: '1.5px solid #d6d6d6', color: '#666', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer' }}>
          Reset demo
        </button>
      </div>
      <p style={{ textAlign: 'center', color: '#999', fontSize: '0.76rem', marginTop: '1rem', marginBottom: 0 }}>
        This is a self-contained simulation of the interaction model, not a connection to a real map or live sensors.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
const AnchorCaseStudy: React.FC = () => {
  useReveal();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const coverRef = useRef<HTMLElement>(null);
  const coverOpacity = useCoverFade(coverRef);

  return (
    <div className="App" style={{ fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .an-reveal { opacity: 0; transform: translateY(26px); transition: opacity 0.65s cubic-bezier(.22,1,.36,1), transform 0.65s cubic-bezier(.22,1,.36,1); }
        .an-reveal.an-revealed { opacity: 1; transform: translateY(0); }
        .an-reveal.d1 { transition-delay: 0.08s; }
        .an-reveal.d2 { transition-delay: 0.16s; }
        .an-reveal.d3 { transition-delay: 0.24s; }
        .an-reveal.d4 { transition-delay: 0.32s; }
        .an-reveal.d5 { transition-delay: 0.40s; }
        .an-hl { background-repeat: no-repeat; background-position: left center; background-size: 0% 88%; transition: background-size 0.75s cubic-bezier(.22,1,.36,1) 0.15s; padding: 0 3px; border-radius: 3px; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
        .an-hl.an-hl-active { background-size: 100% 88%; }
        .an-hl-yellow { background-image: linear-gradient(120deg, #fef08a 100%, transparent 100%); }
        .an-hl-blue   { background-image: linear-gradient(120deg, #bfdbfe 100%, transparent 100%); }
        .an-hl-pink   { background-image: linear-gradient(120deg, #fbcfe8 100%, transparent 100%); }
        .an-hl-teal   { background-image: linear-gradient(120deg, #99f6e4 100%, transparent 100%); }
        .an-pull-quote { font-size: clamp(1.4rem, 3.2vw, 2.1rem); font-weight: 700; line-height: 1.3; letter-spacing: -0.02em; color: #1d1d1f; text-align: center; padding: 3rem 0; border-top: 1px solid #e5e5e5; border-bottom: 1px solid #e5e5e5; margin: 2.5rem 0; }
        .an-pull-quote em { color: #0d9488; font-style: normal; }
        .an-act-num { font-size: clamp(6rem, 15vw, 12rem); font-weight: 800; line-height: 1; letter-spacing: -0.06em; color: rgba(255,255,255,0.05); position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); pointer-events: none; user-select: none; }
        .an-stat-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .an-stat-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(13,148,136,0.14); }
      `}</style>

      <IdentityCard />

      {/* ════════════════════ HERO ════════════════════ */}
      <section style={{ padding: '7.5rem 0 3.5rem', background: '#fff' }}>
        <div className="container">
          <div>
            <div className="an-reveal" style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.3rem' }}>
              <span style={badge('#ccfbf1', '#0f766e')}>Auto-Ethnographic Case Study</span>
              <span style={badge('#f1f5f9', '#475569')}>Product &amp; UI Design Research</span>
              <span style={badge('#f0fdf4', '#166534')}>Working Interactive Prototype</span>
            </div>
            <h1 className="an-reveal d1" style={{ fontSize: 'clamp(2rem, 5vw, 3.1rem)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.04em', marginBottom: '1.1rem', color: '#1d1d1f' }}>
              Anchor: An AI Co-Navigator for Developmental Topographical Disorientation
            </h1>
            <p className="an-reveal d2" style={{ fontSize: '1.12rem', color: '#555', lineHeight: 1.75, marginBottom: '1.5rem' }}>
              I have Developmental Topographical Disorientation: I can read a map perfectly and still not know which way to turn. Every AI wayfinding tool I found, vision assistants, scene-description apps, smart glasses, is built for people who can't see the world. As a PhD candidate in human-AI interaction, I designed for someone who can see it fine and still <Hl color="teal">can't turn it into a direction</Hl>: a speculative AI co-navigator sketched and prototyped across an Apple Watch, an iPhone, and a pair of AirPods.
            </p>
            <div className="an-reveal d3" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
              {['Accessibility', 'Human-AI Collaboration', 'Wearables', 'Auto-Ethnography', 'Spatial Cognition', 'Wayfinding', 'Solo Researcher + Designer + Developer'].map(t => (
                <span key={t} className="tag">{t}</span>
              ))}
            </div>
            <div className="an-reveal d4" style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              <a href="#prototype" style={{ background: '#0d9488', color: '#fff', fontWeight: 700, padding: '0.7rem 1.6rem', borderRadius: 8, textDecoration: 'none', fontSize: '0.95rem' }}>
                Try the Interactive Prototype &rarr;
              </a>
              <a href="#sources" style={btnOutlineGray}>Sources</a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ STATS ════════════════════ */}
      <section style={{ background: '#f5f5f7', padding: '3rem 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1.1rem' }}>
            {[
              { target: 3, suffix: '', label: 'Devices, No New Hardware', sub: 'Apple Watch + iPhone + AirPods' },
              { target: 3, suffix: '%', label: 'Estimated DTD Prevalence', sub: 'Young adults, PLOS ONE 2022' },
              { target: 0, suffix: '', label: 'Existing Tools Built for DTD', sub: 'Across every product I reviewed' },
              { target: 9, suffix: '', label: 'AI / Assistive Tools Reviewed', sub: 'All target blindness, not this' },
              { target: 1, suffix: '', label: 'Tester', sub: 'Me. Auto-ethnography, not a trial' },
            ].map((s, i) => (
              <div key={s.label} className={`an-reveal an-stat-card d${i + 1}`}
                style={{ background: '#fff', borderRadius: 14, padding: '1.4rem 1.2rem', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <CountStat target={s.target} suffix={s.suffix} />
                <div style={{ fontWeight: 600, fontSize: '0.87rem', marginTop: '0.4rem', color: '#1d1d1f' }}>{s.label}</div>
                <div style={{ fontSize: '0.74rem', color: '#999', marginTop: '0.15rem' }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ COVER DIAGRAM (fades on scroll) ════════════════════ */}
      <section ref={coverRef} style={{ background: '#0a0a0a', padding: '3.5rem 0', position: 'relative' }}>
        <div className="container" style={{ opacity: coverOpacity, transition: 'opacity 0.1s linear' }}>
          <div className="an-reveal" style={{ background: '#0a0a0a', borderRadius: 14, padding: '1rem' }}>
            <DeviceTriadDiagram />
            <p style={{ textAlign: 'center', color: '#666', fontSize: '0.82rem', marginTop: '1rem' }}>
              A concept diagram, not a screenshot of a real app. The center node is the personal difficulty model the AI keeps about you. The watch, phone, and AirPods are its three outputs.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ MY ROLE ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="an-reveal" style={pill}>My Contribution</span>
          <h2 className="an-reveal" style={h2}>Researcher, designer, developer, and the subject</h2>
          <p className="an-reveal" style={prose}>
            There is no team to credit here. I scoped the concept, designed every interaction across all three devices, built the on-screen interactive demo you can click through below, and did the literature review that grounds it. I am also the only person who has clicked through that demo. Nothing here has been built as a physical device or worn or field-tested. That is not a limitation I'm hiding. It's the stage this work is at. <Hl color="yellow">Auto-ethnography means my own disorientation is the design material</Hl>, the same way a participant's lived experience would be in a co-design study. The difference is there's no one else in the room to disagree with me yet, and no real-world trial behind any of it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            {[
              { icon: '🧭', t: 'Subject', d: 'I have Developmental Topographical Disorientation. Every problem statement in this case study comes from my own navigation failures, not a hypothetical user.' },
              { icon: '🎨', t: 'Interaction Designer', d: 'Designed the egocentric haptic vector, the adaptive lead-time logic, the phone AR-lite overlay, and the narration rules for the AI co-pilot.' },
              { icon: '💻', t: 'Developer', d: 'Built the interactive prototype embedded in this page, real state, real interaction logic, not a static mockup.' },
              { icon: '🔬', t: 'Researcher', d: 'Reviewed nine commercial and academic wayfinding tools and the clinical literature on DTD to ground the design and check for prior art.' },
            ].map((r, i) => (
              <div key={r.t} className={`an-reveal d${i + 1}`}
                style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem', border: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: '1.7rem', marginBottom: '0.5rem' }}>{r.icon}</div>
                <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.95rem' }}>{r.t}</div>
                <p style={{ fontSize: '0.86rem', color: '#666', margin: 0, lineHeight: 1.6 }}>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ BACKGROUND ════════════════════ */}
      <section id="background" style={secGray}>
        <div className="container">
          <span className="an-reveal" style={pill}>Background</span>
          <h2 className="an-reveal" style={h2}>What Developmental Topographical Disorientation actually is</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem', alignItems: 'start' }}>
            <div>
              <p className="an-reveal" style={prose}>
                DTD was first documented by Giuseppe Iaria and colleagues in 2009, describing a lifelong inability to navigate with no brain injury and no other cognitive impairment <sup>[1]</sup>. A 2022 study puts a number on it: <Hl color="teal">&ldquo;the disorder is present in 3% of the sample&rdquo;</Hl> of 1,698 young Italian adults <sup>[2]</sup>.
              </p>
              <p className="an-reveal" style={prose}>
                The clinical definition is precise about what it isn't: &ldquo;DTD consists of a neurodevelopmental disorder affecting the ability to orient in the environment despite well-preserved cognitive functions, and in the absence of a brain lesion or other neurological or psychiatric conditions&rdquo; <sup>[2]</sup>. Vision is fine. Memory is fine. <Hl color="yellow">Building and using a mental map of space is not</Hl>.
              </p>
              <p className="an-reveal" style={prose}>
                One line from the clinical literature describes the lived experience better than I can: &ldquo;some DTD report that in order not to become lost, they even avoid going to the bathroom for hours if they are in unfamiliar places&rdquo; <sup>[3]</sup>. The same paper notes that &ldquo;individuals with DTD live with a condition that is difficult to share with others because it is little understood, even in the medical field&rdquo; <sup>[3]</sup>.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                { head: 'Every AI wayfinding tool I found targets a different deficit', body: 'Be My AI, Envision, Soundscape, Biped, Glidance, GoodMaps, WeWALK, all built for blindness or low vision. They assume sight is the missing channel. For DTD, sight is intact; the missing piece is turning what you see into a direction.' },
                { head: 'Apple already ships the right sensors, pointed at the wrong problem', body: 'Watch haptic turn tones, Compass, Backtrack, Door Detection in Magnifier, real, shipping features. All built around forward-route confirmation for someone who can already hold a mental map. None offer a continuous "where is back" signal.' },
                { head: 'No system targeting DTD specifically turned up in my search', body: 'I could not find a commercial product or published paper combining a watch+phone+earbuds ecosystem with a learning AI agent for DTD. That is a gap in what I could find, not proof nothing exists, but it is a real, citable gap.' },
              ].map((c, i) => (
                <div key={c.head} className={`an-reveal d${i + 1}`}
                  style={{ background: '#fff', borderRadius: 12, padding: '1.1rem 1.3rem', borderLeft: '3px solid #0d9488' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1d1d1f', marginBottom: '0.35rem' }}>{c.head}</div>
                  <p style={{ fontSize: '0.84rem', color: '#666', margin: 0, lineHeight: 1.58 }}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ COMPARISON TABLE ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="an-reveal" style={pill}>Where this sits in the landscape</span>
          <h2 className="an-reveal" style={h2}>Nine tools, one target population, and the gap</h2>
          <p className="an-reveal" style={prose}>
            Every AI-assisted wayfinding tool I could find, commercial or academic, is built for blindness or low vision, a sensory deficit. None target topographical disorientation, a spatial-cognition deficit, which is a different problem even though the symptom (&ldquo;I don't know which way to go&rdquo;) sounds the same from the outside.
          </p>
          <div className="an-reveal" style={{ overflowX: 'auto', marginTop: '1.2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead><tr style={{ background: '#fff' }}>
                <th style={th}>Tool</th><th style={th}>Target population</th><th style={th}>Modality</th><th style={th}>Targets spatial-memory disorientation?</th>
              </tr></thead>
              <tbody>
                {[
                  ['Microsoft Soundscape (discontinued)', 'Blind / low vision', 'Spatial audio, points of interest', 'No'],
                  ['Be My AI (Be My Eyes)', 'Blind / low vision', 'Photo-based AI scene description', 'No'],
                  ['Envision Glasses / app', 'Blind / low vision', 'Camera + audio scene description', 'No'],
                  ['Biped.ai', 'Blind / low vision', 'Depth cameras + directional audio', 'No'],
                  ['Glidance Glide', 'Blind / low vision', 'Robotic guide + haptic + audio', 'No'],
                  ['GoodMaps', 'Blind / low vision', 'Indoor LiDAR positioning + voice', 'No'],
                  ['WeWALK Smart Cane', 'Blind / low vision', 'Ultrasonic + turn-by-turn voice', 'No'],
                  ['Apple Watch Maps haptics', 'General / sighted users', 'Tone-pattern haptics, forward route only', 'No'],
                  ['Anchor (this project)', 'Developmental Topographical Disorientation', 'Egocentric haptic vector + AR-lite overlay + adaptive narration', 'Yes, by design'],
                ].map((row, i) => (
                  <tr key={row[0]} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', fontWeight: row[0].startsWith('Anchor') ? 700 : 400 }}>
                    <td style={{ ...td, color: row[0].startsWith('Anchor') ? '#0d9488' : '#1d1d1f' }}>{row[0]}</td>
                    <td style={td}>{row[1]}</td>
                    <td style={td}>{row[2]}</td>
                    <td style={{ ...td, color: row[3] === 'No' ? '#999' : '#0d9488', fontWeight: 700 }}>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="an-reveal" style={{ ...prose, marginTop: '1rem', color: '#888', fontStyle: 'italic' }}>
            Sources for this table are listed at the bottom of the page <sup>[5–13]</sup>. A search not finding prior art isn't proof none exists, but I looked, and this is what I found.
          </p>
        </div>
      </section>

      {/* ════════════════════ ACT 1 DIVIDER ════════════════════ */}
      <div style={{ position: 'relative', background: '#0a0a0a', padding: '5rem 0', overflow: 'hidden', textAlign: 'center' }}>
        <div className="an-act-num">01</div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="an-reveal" style={{ color: '#2dd4bf', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', marginBottom: '0.7rem' }}>ACT ONE</div>
          <h2 className="an-reveal" style={{ color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, maxWidth: 600, margin: '0 auto 0.8rem' }}>
            The mismatch nobody else was solving
          </h2>
          <p className="an-reveal" style={{ color: '#777', maxWidth: 440, margin: '0 auto', fontSize: '1rem' }}>Not a vision problem. A translation problem.</p>
        </div>
      </div>

      {/* ════════════════════ ACT 1 CONTENT ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 760 }}>
          <p className="an-reveal" style={prose}>
            I don't have trouble seeing a map. I have trouble doing the thing that's supposed to happen right after: taking a flat, north-up abstraction and turning it into a decision about which way my body should move through a real, three-dimensional place.
          </p>
          <p className="an-reveal" style={prose}>Here's what that actually looks like, on foot:</p>

          <blockquote className="an-reveal an-pull-quote">
            &ldquo;I cannot visualize what I see in the 2D map, especially when I have to combine direction and spatial movement with it, <em>onto my 3D real-world.</em>&rdquo;
          </blockquote>

          <p className="an-reveal" style={prose}>
            It gets worse than just confusion. It produces a specific, repeatable physical behavior:
          </p>

          <blockquote className="an-reveal an-pull-quote">
            &ldquo;It seems like I am standing on the spot sometimes, trying to figure out what way to go, then I have to spin around to figure out the blue ray cast direction <em>and match that with the blue marked line.</em>&rdquo;
          </blockquote>

          <p className="an-reveal" style={prose}>
            That spin is the tell. Every map-based app, Apple Maps, Google Maps, the BLV-focused AI tools I reviewed, renders direction <Hl color="teal">relative to the map</Hl>, then expects me to rotate my own body and my own attention until they line up. That rotation is exactly the transformation DTD breaks. I'm not failing to read the map. I'm failing at the step after reading it.
          </p>
          <p className="an-reveal" style={{ ...prose, fontWeight: 700, color: '#1d1d1f', fontSize: '1.08rem' }}>
            So the design question stopped being &ldquo;how do I show direction more clearly on a map&rdquo; and became <Hl color="yellow">&ldquo;how do I never make the user hold a map-to-body transformation in their head at all?&rdquo;</Hl> Everything below follows from that one shift.
          </p>
        </div>
      </section>

      {/* ════════════════════ DESIGN DECISION: EGOCENTRIC vs ALLOCENTRIC ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="an-reveal" style={pill}>Before a single line of code</span>
          <h2 className="an-reveal" style={h2}>Two reference frames. One that I can actually use.</h2>
          <p className="an-reveal" style={prose}>
            Almost every navigation interface, including the accessibility-focused ones, is built on an <Hl color="blue">allocentric</Hl> reference frame: direction described relative to a fixed external frame like north, or relative to the map itself. The alternative is <Hl color="teal">egocentric</Hl>: direction described relative to wherever my body happens to be facing, right now, with no translation step.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div className="an-reveal d1" style={{ background: '#fff5f5', borderRadius: 12, padding: '1.2rem', border: '1px solid #fecaca', textAlign: 'center' }}>
              <AllocentricIcon />
              <div style={{ fontWeight: 800, color: '#dc2626', margin: '0.6rem 0 0.4rem', fontSize: '0.88rem' }}>Allocentric, what everything else builds</div>
              <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.6, textAlign: 'left' }}>
                &ldquo;Turn left in 200 feet,&rdquo; a blue line on a north-up map, a compass heading. All of it requires me to first work out which way I'm facing, then mentally rotate the instruction to match. <strong>That rotation is the disability.</strong>
              </p>
            </div>
            <div className="an-reveal d2" style={{ background: '#f0fdfa', borderRadius: 12, padding: '1.2rem', border: '1px solid #5eead4', textAlign: 'center' }}>
              <EgocentricIcon />
              <div style={{ fontWeight: 800, color: '#0d9488', margin: '0.6rem 0 0.4rem', fontSize: '0.88rem' }}>Egocentric, chosen</div>
              <p style={{ fontSize: '0.86rem', color: '#555', margin: 0, lineHeight: 1.6, textAlign: 'left' }}>
                Every output is phrased as &ldquo;from where you are facing right now.&rdquo; No map is ever shown. The watch needle, the phone overlay, and the spoken cue all update continuously so they're always already in my frame. <strong>No rotation required, because there is nothing left to rotate.</strong>
              </p>
            </div>
          </div>
          <p className="an-reveal" style={{ ...prose, marginTop: '1.2rem', color: '#888', fontStyle: 'italic' }}>
            This single decision is the spine of the whole system. Every device, every channel, every line of the AI's narration logic exists to keep the interface in my frame instead of the map's.
          </p>
        </div>
      </section>

      {/* ════════════════════ UI DESIGN RATIONALE ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 780 }}>
          <span className="an-reveal" style={pill}>UI Design Rationale</span>
          <h2 className="an-reveal" style={h2}>Three small decisions that carry the whole interface</h2>
          <p className="an-reveal" style={prose}>
            A reference frame decision does not, by itself, produce a usable interface. It has to be carried through typography, color, and motion choices that are easy to skip past in a write-up but are where most of the actual interaction design happened. Three of them are worth naming on their own.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem', marginTop: '1.2rem' }}>
            {[
              { t: 'Shape before text, everywhere', b: 'The watch dial and the AR overlay lead with a triangle and a position, not a sentence. Reading a word still requires the same allocentric to egocentric translation that breaks for me. A shape pointed in my own facing direction does not. Text exists only as a secondary confirmation, never as the primary channel.' },
              { t: 'Color is a state, not a decoration', b: 'Teal means the system is confident and the cue is routine. Amber means a known hard point is approaching. Red means the window to act is closing now. The same three states repeat identically across the watch, the phone, and the difficulty model list, so the meaning of a color never has to be relearned per screen.' },
              { t: 'Motion announces urgency before the words do', b: 'The pulsing ring around the watch needle and the live activity strip on the phone both escalate their animation rate as a decision point gets closer. That escalation is read peripherally, while attention is still on the road or the sidewalk, which is the entire point of putting it on a wrist and a lock screen instead of inside an app you have to be looking at.' },
            ].map((c, i) => (
              <div key={c.t} className={`an-reveal d${i + 1}`} style={{ background: '#f9f9fb', borderRadius: 12, padding: '1.2rem', borderLeft: '3px solid #0d9488' }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1d1d1f', marginBottom: '0.4rem' }}>{c.t}</div>
                <p style={{ fontSize: '0.84rem', color: '#666', margin: 0, lineHeight: 1.6 }}>{c.b}</p>
              </div>
            ))}
          </div>
          <p className="an-reveal" style={{ ...prose, marginTop: '1.2rem', color: '#888', fontStyle: 'italic' }}>
            None of these three choices required new technology. They required treating the interface, not the route, as the thing being designed, which is the product design argument this whole case study is making.
          </p>
        </div>
      </section>

      {/* ════════════════════ ACT 2 DIVIDER ════════════════════ */}
      <div style={{ position: 'relative', background: '#0a0a0a', padding: '5rem 0', overflow: 'hidden', textAlign: 'center' }}>
        <div className="an-act-num">02</div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="an-reveal" style={{ color: '#2dd4bf', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', marginBottom: '0.7rem' }}>ACT TWO</div>
          <h2 className="an-reveal" style={{ color: '#fff', fontSize: 'clamp(1.7rem, 4vw, 2.8rem)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, maxWidth: 680, margin: '0 auto 0.8rem' }}>
            Building Anchor
          </h2>
          <p className="an-reveal" style={{ color: '#777', maxWidth: 560, margin: '0 auto 2rem', fontSize: '1rem' }}>
            Three real problems, each traced to its own device.
          </p>
          <div className="an-reveal" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            {['Problem 01: the translation tax', 'Problem 02: driving doesn’t forgive lag', 'Problem 03: wanting the world annotated directly'].map(p => (
              <div key={p} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '0.5rem 1rem', color: '#ccc', fontSize: '0.82rem', fontWeight: 500 }}>{p}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════ PROBLEM 1: WATCH ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="an-reveal" style={problemBadge}>Problem 01 · Apple Watch</div>
          <h2 className="an-reveal" style={h2}>The translation tax</h2>
          <p className="an-reveal" style={prose}>
            The &ldquo;spin around to match the blue line&rdquo; moment from Act One is the translation tax: the cost of converting an allocentric instruction into an egocentric action, paid out loud, on the sidewalk, every single time. Apple Watch already has a compass, a gyroscope, and a haptic engine. It already plays directional tone patterns for turns <sup>[4]</sup>. What it doesn't do is give a <em>continuous</em> sense of where you are relative to where you're trying to go.
          </p>
          <div className="an-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' }}>The fix: a haptic vector instead of a haptic event</div>
            <p style={{ margin: 0, lineHeight: 1.65, color: '#444' }}>
              Instead of a tone pattern that fires once at a turn, the watch face renders a needle that always points to true bearing, recalculated continuously from the watch's own compass and gyroscope. <Hl color="teal">Turn your wrist, spin your whole body, it doesn't matter</Hl>. The needle re-locks instantly. There's nothing to read and nothing to rotate in your head. You just follow the pull, the way you'd follow a hand on your shoulder.
            </p>
          </div>
          <div className="an-reveal" style={{ textAlign: 'center', margin: '1.5rem 0' }}>
            <WatchDial angle={-35} size={150} />
          </div>
          <p className="an-reveal" style={{ ...prose, color: '#888', fontStyle: 'italic' }}>
            I have not built or worn a physical haptic watch face that does this. This is a designed interaction, demonstrated on screen below, not a device I have field-tested on a real sidewalk.
          </p>
        </div>
      </section>

      {/* ════════════════════ COUNTING, NOT MEASURING ════════════════════ */}
      <section style={secGray}>
        <div className="container" style={{ maxWidth: 780 }}>
          <span className="an-reveal" style={pill}>The actual collaboration</span>
          <h2 className="an-reveal" style={h2}>Counting, not measuring</h2>
          <p className="an-reveal" style={prose}>
            The haptic vector fixes the reference frame. It does not, by itself, fix the language. Every example so far could still have ended with a distance: &ldquo;the needle points there, in twenty feet.&rdquo; I genuinely did not want that, and the reason is worth stating as its own design decision rather than folding it into the watch section above.
          </p>
          <p className="an-reveal" style={prose}>
            A distance is a second translation problem disguised as a solution. Knowing a turn is &ldquo;180 feet away&rdquo; still asks me to convert a number into a felt sense of when to act, which is exactly the kind of abstract-to-embodied conversion that breaks for DTD in the first place. What I actually use, on my own without any system, is counting: not this door, the next one. Not this exit, two after. So that is what Anchor says, every time, instead of a unit of distance.
          </p>
          <div className="an-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' }}>Where the AI collaboration actually lives</div>
            <p style={{ margin: 0, lineHeight: 1.65, color: '#444' }}>
              Counting only works if something is continuously checking which landmark you are oriented toward right now, and that is the genuine human-AI collaboration in this system, more than any single device. The watch's gyroscope and compass, and the phone's equivalent sensors in CarPlay, feed a constant heading stream. Anchor compares that heading against the route graph in real time and asks one question, repeatedly: is this person's body currently turning toward the correct landmark, or the wrong one. <Hl color="teal">When the answer is &ldquo;the wrong one,&rdquo; the correction fires before the mistake completes</Hl>, phrased the same way a person walking next to me would phrase it: &ldquo;not that one, the next one.&rdquo; A generic GPS distance cannot do this, because GPS alone has no idea which way your body is actually facing at this instant. The collaboration is the AI watching my heading the way a co-pilot watches the road, and saying something the moment it notices me drifting wrong, not on a fixed schedule.
            </p>
          </div>
          <p className="an-reveal" style={{ ...prose, color: '#888', fontStyle: 'italic' }}>
            This is the piece of the system I most want a future co-design study to pressure-test: whether ordinal counting holds up across longer routes, and across other people's versions of this same disorientation.
          </p>
        </div>
      </section>

      {/* ════════════════════ PROBLEM 2: AI + LEAD TIME ════════════════════ */}
      <section style={secGray}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="an-reveal" style={problemBadge}>Problem 02 · the AI co-pilot</div>
          <h2 className="an-reveal" style={h2}>Driving doesn't forgive lag</h2>
          <p className="an-reveal" style={prose}>Walking gives you a second chance to stop and reorient. Driving doesn't:</p>

          <blockquote className="an-reveal an-pull-quote">
            &ldquo;This is so much worse when I drive. Entrances are not the most obvious to me, so I end up turning too late, missing exits or even realizing too late to change lanes. <em>Leaving parking lots is a nightmare.</em>&rdquo;
          </blockquote>

          <p className="an-reveal" style={prose}>
            A generic &ldquo;turn left in 200 feet&rdquo; assumes every driver needs the same amount of lead time at every decision point. That assumption is exactly what fails for DTD: some intersections are trivial, and some, a hidden garage exit, a butterfly interchange, need far more lead time than any generic system gives anyone, because the failure mode isn't slow reflexes, it's a translation step that takes longer under time pressure.
          </p>
          <div className="an-reveal" style={lightbulbBox}>
            <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>💡</div>
            <div style={{ fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' }}>The fix: a personal difficulty model, not a generic alert radius</div>
            <p style={{ margin: 0, lineHeight: 1.65, color: '#444' }}>
              This is the actual human-AI collaboration in the system: the AI doesn't just execute a route, it keeps a per-decision-point memory of where <em>this specific person</em> has hesitated, missed a turn, or doubled back before. The next time that decision point comes up, the lead time, the repetition, and the amount of detail in the cue all scale up automatically. Two different drivers with two different histories get two different warnings at the exact same intersection.
            </p>
          </div>
          <div className="an-reveal" style={{ margin: '1.5rem 0' }}>
            <LeadTimeChart />
          </div>

          <h3 className="an-reveal" style={{ ...h3, marginTop: '2.4rem', color: '#0d9488' }}>CarPlay: the windscreen becomes the fourth surface</h3>
          <p className="an-reveal" style={prose}>
            The watch and AirPods carry the driving experience hands free, but a car already has a screen built into the dashboard, and ignoring it would mean throwing away the one surface large enough to show structure, not just a single arrow. CarPlay is where Anchor draws the actual interchange: two looping ramps, the bridge deck between them, the specific path painted directly onto the correct ramp.
          </p>
          <div className="an-reveal" style={{ margin: '1.5rem 0' }}>
            <CarPlayDashboard />
          </div>
          <p className="an-reveal" style={prose}>
            Notice the caption underneath still counts rather than measures: <Hl color="teal">stay right onto the lower ramp</Hl>, not a distance to a ramp. The car's own compass and the phone's gyroscope together establish which ramp the vehicle's heading is actually drifting toward as the interchange approaches, so if the wheel starts turning toward the upper loop, the correction fires before the lane change, in the same ordinal language as the watch and the phone: not this ramp, the lower one.
          </p>
        </div>
      </section>

      {/* ════════════════════ PROBLEM 3: PHONE AR-LITE ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 780 }}>
          <div className="an-reveal" style={problemBadge}>Problem 03 · iPhone</div>
          <h2 className="an-reveal" style={h2}>Wanting the world annotated directly</h2>
          <p className="an-reveal" style={prose}>This is the clearest design brief I could have written for myself, so I'll just use it as one:</p>

          <blockquote className="an-reveal an-pull-quote">
            &ldquo;I wish someone would just clearly granularly indicate as a super imposition on my world, where exactly to turn, there is honestly nothing else I need. <em>Point to me exactly, where what is, and I'll go.</em> Not abstract turn left in 200 ft or take the next entrance.&rdquo;
          </blockquote>

          <p className="an-reveal" style={prose}>
            That request is, precisely, AR glasses. I don't have AR glasses. What I have is a phone camera. So the iPhone's job in this system is to be the closest honest approximation: hold it up, and the live camera view gets a bold arrow and a short label burned directly onto whatever you're actually looking at, not a map, not a separate screen to cross-reference, the real doorway or ramp itself with the instruction stuck to it.
          </p>
          <p className="an-reveal" style={prose}>
            The overlay is also where the no-distance rule gets its visual proof. The street itself is rendered, simplified, extruded buildings on either side rather than flat lines, because a real street has corners and storefronts to count, not a number on a screen. One building gets a teal outline, the way the doorway does in the glasses storyboard further down: not a measurement, a pointed-at thing. The label underneath only ever counts: <Hl color="teal">not this one, the next</Hl>.
          </p>
          <div className="an-reveal" style={{ margin: '1.5rem 0' }}>
            <PhoneOverlay angle={-30} label="Not this corner. The next one, past the highlighted building." />
          </div>
          <p className="an-reveal" style={prose}>
            It's also the answer to a second thing I noticed about myself: <Hl color="yellow">I don't usually mix up verbal terms, but I am a visual learner</Hl>, and while walking, where holding a phone up is natural, a visual anchor sticks better than another sentence in my ear. Driving hands-free is the AirPods and watch's job; walking with a hand free is the phone's.
          </p>
        </div>
      </section>

      {/* ════════════════════ HUMAN-AI COLLABORATION ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 820 }}>
          <span className="an-reveal" style={pill}>Human-AI Collaboration</span>
          <h2 className="an-reveal" style={h2}>What collaboration actually means in this system</h2>
          <p className="an-reveal" style={prose}>
            It would be easy to call any of the three problems above &ldquo;AI-powered&rdquo; and leave it at that. I want to be specific instead, because the phrase is overused to the point of meaning almost nothing. In Anchor, human-AI collaboration is not a single feature. It is four distinct relationships between me and the system, and each one does a different job.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '1.4rem' }}>
            <div className="an-reveal d1" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem 1.6rem', borderLeft: '4px solid #0d9488' }}>
              <div style={{ fontWeight: 800, fontSize: '0.98rem', color: '#1d1d1f', marginBottom: '0.5rem' }}>1. Intent inference, not command execution</div>
              <p style={{ margin: 0, color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
                A standard navigation app executes a script: it reads turns off a route in order, regardless of what I am actually doing. Anchor instead treats my real-time heading, read continuously from the watch's gyroscope and compass, as a signal of <em>intent</em>. It is constantly asking one question: does the direction my body is currently turning toward match the correct landmark, or a wrong one. The collaboration is in that inference step. The AI is not telling me what the route says. It is watching what I am about to do and stepping in only when those two things disagree.
              </p>
            </div>
            <div className="an-reveal d2" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem 1.6rem', borderLeft: '4px solid #0d9488' }}>
              <div style={{ fontWeight: 800, fontSize: '0.98rem', color: '#1d1d1f', marginBottom: '0.5rem' }}>2. Shared memory, not hidden personalization</div>
              <p style={{ margin: 0, color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
                Most personalized AI systems build a model of you that you never see. The personal difficulty model in Anchor is the opposite: it is rendered as a plain, editable list on the phone, the same screen shown in the iPhone exploration section above. If the AI decides a ramp is hard for me and it is wrong, I can see that belief and turn it off. Collaboration requires both parties to be able to correct the record, not just one.
              </p>
            </div>
            <div className="an-reveal d3" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem 1.6rem', borderLeft: '4px solid #0d9488' }}>
              <div style={{ fontWeight: 800, fontSize: '0.98rem', color: '#1d1d1f', marginBottom: '0.5rem' }}>3. A channel that talks back, not just one that talks at me</div>
              <p style={{ margin: 0, color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
                The AirPods query card earlier in this page, a double tap that opens &ldquo;Anchor, where is my car from here,&rdquo; exists because collaboration has to run in both directions. A system that only ever pushes instructions at me is an announcement system, not a collaborator. The moment I can ask it a question mid-route and get an answer scoped to exactly where I am standing is the moment it starts to resemble a person walking next to me, rather than a recording.
              </p>
            </div>
            <div className="an-reveal d4" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.4rem 1.6rem', borderLeft: '4px solid #0d9488' }}>
              <div style={{ fontWeight: 800, fontSize: '0.98rem', color: '#1d1d1f', marginBottom: '0.5rem' }}>4. Choosing the right device, not repeating the same message on all of them</div>
              <p style={{ margin: 0, color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
                The watch, phone, and AirPods never say the identical thing at the identical moment. Anchor decides which surface carries which part of the correction: a felt pull on the wrist when my hands are busy, a painted ramp on CarPlay when there is room to show structure, a spoken reason in my ear when I need to know why, not just what. That allocation decision, made fresh every time based on what I am doing, hands free or not, walking or driving, is itself a form of collaboration: the AI deciding how to reach me, not just what to tell me.
              </p>
            </div>
          </div>

          <p className="an-reveal" style={{ ...prose, marginTop: '1.6rem', color: '#888', fontStyle: 'italic' }}>
            None of these four require a more powerful model. They require treating the AI as a participant with its own observations, its own memory, and its own judgment about which channel to use, rather than a script reader with a synthetic voice. That distinction is the actual subject of this case study, more than any single device.
          </p>
        </div>
      </section>

      {/* ════════════════════ SYSTEM ARCHITECTURE / AIRPODS RATIONALE ════════════════════ */}
      <section style={secGray}>
        <div className="container">
          <span className="an-reveal" style={pill}>System Architecture</span>
          <h2 className="an-reveal" style={h2}>Why the AI narrates, instead of just commands</h2>
          <p className="an-reveal" style={prose}>
            The AirPods channel is where the &ldquo;collaboration&rdquo; in human-AI collaboration actually shows up. A turn-by-turn system tells you what to do. A co-pilot tells you what's coming, why, and whether you're still on track, unprompted, before you have to ask. That distinction matters most exactly where it's hardest to hold a mental map:
          </p>
          <blockquote className="an-reveal an-pull-quote">
            &ldquo;I also want to know what is coming next, why we are turning, and if we are still on the right path, <em>especially on bridges and butterfly turns,</em> it is very confusing.&rdquo;
          </blockquote>
          <p className="an-reveal" style={prose}>
            So the narration logic flags structurally ambiguous segments: interchanges with multiple stacked ramps, bridges with no landmarks, anywhere the road geometry itself is disorienting, and proactively narrates three things at once: the upcoming maneuver, the reason for it (&ldquo;because the bridge splits here&rdquo;), and an explicit on-track confirmation, spatially panned left or right in the audio so the direction is felt, not just described.
          </p>
          <p className="an-reveal" style={prose}>
            On a CarPlay screen, where there is room for more than a single arrow, that means drawing the actual interchange geometry, the two looping ramps and the bridge deck between them, with the AI's chosen path painted directly onto it instead of abstracted into a generic &ldquo;take the ramp&rdquo; instruction, exactly as shown in the CarPlay section above.
          </p>
          <div className="an-reveal" style={{ margin: '1.5rem 0 0.5rem' }}>
            <SystemLoopDiagram />
          </div>
          <p className="an-reveal" style={{ ...prose, color: '#888', fontStyle: 'italic', marginTop: '0.8rem' }}>
            The loop closes on the left: every hesitation, correction, or missed turn updates the personal difficulty model, which is what makes the lead time behavior in Problem 02 possible.
          </p>
        </div>
      </section>

      {/* ════════════════════ EXPLORE THE SYSTEM: 3 STATES PER DEVICE ════════════════════ */}
      <section style={secWhite}>
        <div className="container">
          <span className="an-reveal" style={pill}>Product &amp; UI Design</span>
          <h2 className="an-reveal" style={h2}>Each device does more than one thing</h2>
          <p className="an-reveal" style={prose}>
            It would be easy to read Problems 01 through 03 as &ldquo;the watch does X, the phone does Y, the AirPods do Z&rdquo; and stop there. That undersells the actual interface design work. Each device carries a small range of states tuned to what it is good at: glanceable confirmation, hands free narration, or a moment of deliberate review. Below is that fuller range, not just the single state used in each problem write up.
          </p>

          <h3 className="an-reveal" style={{ ...h3, marginTop: '2.2rem', color: '#0d9488' }}>Apple Watch: three faces, one purpose</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1.6rem', marginTop: '1rem' }}>
            {[
              { node: <WatchFaceVector angle={-35} />, t: 'Vector face', d: 'The default state while moving. A continuous haptic and visual bearing, recalculated as you turn, used throughout Problem 01.' },
              { node: <WatchFaceEta />, t: 'Counting face', d: 'A glanceable correction read from the gyroscope: which one, not how far. Surfaces only when your heading drifts toward the wrong choice.' },
              { node: <WatchFaceModel />, t: 'Memory face', d: 'A short list of the decision points Anchor already treats as difficult for you specifically, raised by a firm press, not shown by default.' },
            ].map((c, i) => (
              <div key={c.t} className={`an-reveal d${i + 1}`} style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>{c.node}</div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', marginTop: '0.8rem', color: '#1d1d1f' }}>{c.t}</div>
                <p style={{ fontSize: '0.82rem', color: '#666', marginTop: '0.3rem', lineHeight: 1.5 }}>{c.d}</p>
              </div>
            ))}
          </div>

          <h3 className="an-reveal" style={{ ...h3, marginTop: '2.8rem', color: '#0d9488' }}>iPhone and CarPlay: from overlay to oversight</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1.6rem', marginTop: '1rem' }}>
            {[
              { node: <PhoneScreenAR angle={-30} label="Not this corner. The next one." />, t: 'Live overlay', d: 'The camera passthrough mode from Problem 03, used while walking with a hand free, for the moment a direct point is worth more than a sentence.' },
              { node: <PhoneScreenLiveActivity />, t: 'Ambient live activity', d: 'A persistent island style strip over the lock screen, so the next instruction is visible without opening Anchor at all, the phone equivalent of a glance at the watch.' },
              { node: <PhoneScreenModel />, t: 'Difficulty model, editable', d: 'The full personal difficulty model as a plain settings list. You can see exactly what Anchor believes about you, and turn any entry off if it is wrong.' },
              { node: <div style={{ transform: 'scale(0.78)', transformOrigin: 'top center' }}><CarPlayDashboard /></div>, t: 'CarPlay dashboard', d: 'The widescreen state for driving, introduced in Problem 02. The interchange geometry is drawn for real, with the chosen ramp painted onto it directly, counted rather than measured.' },
            ].map((c, i) => (
              <div key={c.t} className={`an-reveal d${i + 1}`} style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>{c.node}</div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', marginTop: '0.8rem', color: '#1d1d1f' }}>{c.t}</div>
                <p style={{ fontSize: '0.82rem', color: '#666', marginTop: '0.3rem', lineHeight: 1.5 }}>{c.d}</p>
              </div>
            ))}
          </div>

          <h3 className="an-reveal" style={{ ...h3, marginTop: '2.8rem', color: '#0d9488' }}>AirPods: narrate, answer, and protect attention</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.6rem', marginTop: '1rem' }}>
            {[
              { node: <AirpodsBubble text="Exit ramp, sharp left." why="This is the ramp you have missed twice before." side="left" />, t: 'Proactive narration', d: 'Unprompted, spatially panned cues at decision points, with the reasoning attached, the channel used throughout the system architecture section above.' },
              { node: <AirPodsQueryCard />, t: 'Conversational query', d: 'A double tap on the stem opens a question instead of waiting for a cue. This is the one channel where the collaboration runs in both directions.' },
              { node: <AirPodsModeCard />, t: 'Context aware hearing mode', d: 'Transparency mode stays on by default while you are moving, so ambient traffic and pedestrian sound are never replaced by the AI\'s voice. A real, shipping AirPods feature, repurposed for safety.' },
            ].map((c, i) => (
              <div key={c.t} className={`an-reveal d${i + 1}`}>
                {c.node}
                <div style={{ fontWeight: 700, fontSize: '0.88rem', marginTop: '0.8rem', color: '#1d1d1f' }}>{c.t}</div>
                <p style={{ fontSize: '0.82rem', color: '#666', marginTop: '0.3rem', lineHeight: 1.5 }}>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ INTERACTIVE PROTOTYPE ════════════════════ */}
      <section id="prototype" style={{ background: '#0a0a0a', padding: '4.5rem 0' }}>
        <div className="container">
          <h2 className="an-reveal" style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(1.5rem, 3vw, 2rem)', textAlign: 'center', marginBottom: '0.6rem', letterSpacing: '-0.02em' }}>Try it</h2>
          <p className="an-reveal" style={{ color: '#999', textAlign: 'center', marginBottom: '2.2rem', fontSize: '0.94rem', maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
            Walk through a short simulated route. Step three is the parking garage exit from Problem 02. The first pass is deliberately too late, exactly like the real one. Watch what changes the second time through.
          </p>
          <div className="an-reveal" style={{ maxWidth: 760, margin: '0 auto' }}>
            <AnchorPrototype />
          </div>
        </div>
      </section>

      {/* ════════════════════ VALIDATION / HONESTY ════════════════════ */}
      <section style={secWhite}>
        <div className="container" style={{ maxWidth: 760 }}>
          <span className="an-reveal" style={pill}>Validation, and its limits</span>
          <h2 className="an-reveal" style={h2}>This is design and speculation, not a tested system</h2>
          <p className="an-reveal" style={prose}>
            I want to be precise about what exists here and what doesn't. The interactive demo on this page is a working illustration of an interaction model, built so the idea can be clicked through rather than only described in prose. It is not a haptic watch face I have worn, not an overlay I have walked or driven with, and not a study with participants. Nothing in this case study should be read as &ldquo;I tried this and it worked.&rdquo; It is &ldquo;I designed this, here is why, and here is what it would look like.&rdquo;
          </p>
          <p className="an-reveal" style={prose}>
            The design recommendations and interface decisions here draw on my own lived experience with DTD and on insights from a paper I have in submission on a related topic. If that paper is accepted, I will link it here and update this case study to reflect it directly. Until then, treat the reasoning in this page as informed and motivated, not as a validated result.
          </p>
          <p className="an-reveal" style={prose}>
            DTD is also not one thing. The clinical literature that gives the 3% prevalence figure found that navigational strategies vary by individual <sup>[2]</sup>. Some people described in that literature show topographic memory deficits, others landmark agnosia <sup>[2]</sup>. A haptic vector and an AR-lite overlay are <Hl color="teal">my</Hl> proposed fix for <Hl color="teal">my</Hl> specific failure point: the map-to-body transformation. Someone whose DTD presents differently might need a different intervention entirely.
          </p>
          <div className="an-reveal" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.6rem', borderLeft: '4px solid #0d9488' }}>
            <p style={{ fontWeight: 700, color: '#0d9488', marginBottom: '0.6rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What would make this real evidence</p>
            <p style={{ fontSize: '0.95rem', color: '#1d1d1f', lineHeight: 1.7, margin: 0 }}>
              A small co-design study with other people who have DTD, direct quotes traced to specific features, iterated against real disagreement, and an actual built device worn or held during real navigation. None of that has happened yet. This case study is the design proposal and the argument for why that study is worth running, not a substitute for it.
            </p>
          </div>

          {/* ════════ REFLECTIONS (same section, no extra white gap) ════════ */}
          <span className="an-reveal" style={{ ...pill, marginTop: '3rem' }}>Reflections</span>
          <h2 className="an-reveal" style={h2}>What designing for one person, myself, actually taught me</h2>
          <p className="an-reveal" style={prose}>
            Every other case study I have conducted started with someone else's lived experience and my job was to listen carefully enough to build the right thing. This one inverted that: I had to be both the person describing the problem and the person deciding whether the proposed fix was honest or just convenient. That's a harder kind of rigor to hold yourself to, not an easier one. There's no collaborator in the room to tell you when you're solving the wrong thing.
          </p>
          <p className="an-reveal" style={prose}>
            The clearest lesson is that <Hl color="teal">the right reference frame matters more than the richness of the information</Hl>. None of these three devices tell you anything a standard map app doesn't already know: distance, bearing, points of interest. The entire design is in refusing to make me do the allocentric-to-egocentric translation myself, and handing that translation to the AI instead.
          </p>
          <div className="an-reveal" style={{ background: '#f9f9fb', borderRadius: 14, padding: '1.6rem', marginTop: '1.5rem', borderLeft: '4px solid #0d9488' }}>
            <p style={{ fontWeight: 700, color: '#0d9488', marginBottom: '0.6rem', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>The line that stuck with me, writing this</p>
            <p style={{ fontSize: '1rem', color: '#1d1d1f', lineHeight: 1.72, margin: 0 }}>
              &ldquo;I usually don't mess up terms, but I am a visual learner.&rdquo; A system built for &ldquo;someone who can't navigate&rdquo; would have leaned entirely on speech. The actual fix needed all three channels precisely because the deficit is narrow and specific, not global, which is the whole argument for designing around one real person instead of an average one.
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════════ FUTURE: SMART GLASSES ════════════════════ */}
      <section style={{ background: '#0a0a0a', padding: '4.5rem 0', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div className="container" style={{ width: '100%' }}>
          <span className="an-reveal" style={{ ...pill, background: 'rgba(45,212,191,0.15)', color: '#5eead4' }}>What Comes Next</span>
          <h2 className="an-reveal" style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', marginBottom: '0.8rem', letterSpacing: '-0.02em' }}>
            The endpoint this whole system is pointing at: glasses
          </h2>
          <p className="an-reveal" style={{ color: '#999', maxWidth: 700, marginBottom: '1.2rem', fontSize: '1rem', lineHeight: 1.75 }}>
            The phone AR-lite overlay in Problem 03 exists because I don't have AR glasses, not because it's the ideal form factor. The actual request, &ldquo;point to me exactly, where what is, and I'll go,&rdquo; superimposed directly on the world, is a hands-free, always-on display problem. Hardware for this is real and shipping today: Envision Glasses run scene description on Google Glass Enterprise hardware, Meta's Ray-Ban glasses already do live AI scene Q&amp;A and real-time translation, and OrCam MyEye runs fully on-device <sup>[6][10][12]</sup>. None of them are built for spatial-memory disorientation. That's the gap Anchor's glasses concept would close.
          </p>
          <p className="an-reveal" style={{ color: '#999', maxWidth: 700, marginBottom: '1.8rem', fontSize: '1rem', lineHeight: 1.75 }}>
            Because I don't have the hardware to build this, the storyboard below is deliberately framed as speculative design rather than a product mockup, a method that uses a plausible near future object to ask what values it would encode, not to claim it is ready to ship. The three frames track one approach to one doorway, at three distances, to make a single argument concrete: the overlay should escalate with proximity, the same way the watch and the phone already do, so that nothing about the glasses requires a new design language to learn.
          </p>
          <div className="an-reveal" style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid #222' }}>
            <GlassesConceptSketch />
          </div>
          <p className="an-reveal" style={{ color: '#666', fontSize: '0.82rem', textAlign: 'center', marginTop: '1rem' }}>
            Concept sketch, not a render of an existing product. The arrow and label are exactly what the phone overlay shows today, moved from a screen in your hand onto the lens in front of your eye, with the watch's haptic vector and the AirPods' narration unchanged underneath it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
            {[
              { t: 'Same model, new output', b: 'The personal difficulty model, the egocentric framing, and the adaptive lead-time logic carry over unchanged. Glasses are a fourth channel, not a redesign.' },
              { t: 'Hands stay free, always', b: 'The one thing the phone overlay can\'t do is work while your hands are full or while you\'re driving. Glasses remove that limitation entirely.' },
              { t: 'Needs its own co-design pass', b: 'Continuous AR overlays in a driver\'s field of view raise real safety and attention questions that a phone held briefly does not. This needs testing before it\'s built, not after.' },
            ].map((f, i) => (
              <div key={f.t} className={`an-reveal d${i + 1}`} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '1.1rem', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.35rem', color: '#e2e8f0' }}>{f.t}</div>
                <p style={{ fontSize: '0.84rem', color: '#999', margin: 0, lineHeight: 1.5 }}>{f.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ SOURCES FOOTER ════════════════════ */}
      <section id="sources" style={{ background: '#0a0a0a', padding: '4rem 0' }}>
        <div className="container">
          <h2 className="an-reveal" style={{ color: '#fff', fontWeight: 800, fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>Sources</h2>
          <ol className="an-reveal" style={{ color: '#999', fontSize: '0.84rem', lineHeight: 1.9, paddingLeft: '1.3rem', margin: 0 }}>
            <li>Iaria, G. et al. (2009). &ldquo;Developmental Topographical Disorientation: a newly discovered cognitive disorder.&rdquo; <a href="https://www.researchgate.net/publication/43355780_Developmental_Topographical_Disorientation_a_newly_discovered_cognitive_disorder" target="_blank" rel="noopener noreferrer" style={srcLink}>ResearchGate</a></li>
            <li>&ldquo;'Where am I?' A snapshot of the developmental topographical disorientation among young Italian adults.&rdquo; PLOS ONE (2022). <a href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0271334" target="_blank" rel="noopener noreferrer" style={srcLink}>journals.plos.org</a></li>
            <li>&ldquo;Where Am I? Searching for the Tangle in the Developmental Topographical Disorientation.&rdquo; <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC9589977/" target="_blank" rel="noopener noreferrer" style={srcLink}>PMC9589977</a></li>
            <li>Apple Support. &ldquo;Get directions in Maps on Apple Watch.&rdquo; <a href="https://support.apple.com/guide/watch/get-directions-apdea7480950/watchos" target="_blank" rel="noopener noreferrer" style={srcLink}>support.apple.com</a></li>
            <li>Apple Support. &ldquo;Use the Compass app on Apple Watch.&rdquo; <a href="https://support.apple.com/guide/watch/use-the-compass-app-apd1cd7aad2c/watchos" target="_blank" rel="noopener noreferrer" style={srcLink}>support.apple.com</a></li>
            <li>Apple Newsroom. &ldquo;Apple previews innovative accessibility features&rdquo; (Door Detection). <a href="https://www.apple.com/newsroom/2022/05/apple-previews-innovative-accessibility-features/" target="_blank" rel="noopener noreferrer" style={srcLink}>apple.com/newsroom</a></li>
            <li>Microsoft Research. &ldquo;Soundscape&rdquo; (discontinued product page). <a href="https://www.microsoft.com/en-us/research/product/soundscape/support/" target="_blank" rel="noopener noreferrer" style={srcLink}>microsoft.com/research</a></li>
            <li>Be My Eyes. &ldquo;Be My AI.&rdquo; <a href="https://www.bemyeyes.com/bme-ai/" target="_blank" rel="noopener noreferrer" style={srcLink}>bemyeyes.com</a></li>
            <li>Envision. Glasses product page. <a href="https://www.letsenvision.com/glasses/home" target="_blank" rel="noopener noreferrer" style={srcLink}>letsenvision.com</a></li>
            <li>Biped.ai. Orientation &amp; Mobility. <a href="https://biped.ai/orientationmobility" target="_blank" rel="noopener noreferrer" style={srcLink}>biped.ai</a></li>
            <li>Glidance. &ldquo;Glide&rdquo; product page. <a href="https://glidance.io/product/" target="_blank" rel="noopener noreferrer" style={srcLink}>glidance.io</a></li>
            <li>Meta. AI Glasses, real-time translation. <a href="https://www.meta.com/ai-glasses/real-time-translation/" target="_blank" rel="noopener noreferrer" style={srcLink}>meta.com</a></li>
            <li>OrCam. MyEye 3 Pro. <a href="https://www.orcam.com/en-us/orcam-myeye-3-pro" target="_blank" rel="noopener noreferrer" style={srcLink}>orcam.com</a></li>
          </ol>
          <p className="an-reveal" style={{ color: '#666', fontSize: '0.78rem', marginTop: '1.5rem', maxWidth: 700 }}>
            Every quote and statistic above is reproduced from one of these sources, linked at the point of use. Where I could not independently verify a claim against a primary source, I left it out rather than include it as unconfirmed.
          </p>
          <div className="an-reveal" style={{ marginTop: '2rem' }}>
            <Link to="/" style={{ color: '#5eead4', fontWeight: 500, fontSize: '0.92rem' }}>&larr; Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SHARED STYLE TOKENS
───────────────────────────────────────────── */
const badge = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, fontSize: '0.77rem', fontWeight: 600,
  padding: '0.22rem 0.65rem', borderRadius: 20,
});
const pill: React.CSSProperties = {
  display: 'inline-block', background: '#ccfbf1', color: '#0f766e',
  fontSize: '0.76rem', fontWeight: 700, padding: '0.2rem 0.65rem',
  borderRadius: 20, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em',
};
const btnOutlineGray: React.CSSProperties = {
  display: 'inline-block', padding: '0.55rem 1.15rem',
  border: '1.5px solid #bbb', color: '#666',
  borderRadius: 8, fontSize: '0.88rem', fontWeight: 500, textDecoration: 'none',
};
const srcLink: React.CSSProperties = { color: '#5eead4' };
const secWhite: React.CSSProperties = { background: '#fff', padding: '5rem 0' };
const secGray: React.CSSProperties  = { background: '#f5f5f7', padding: '5rem 0' };
const h2: React.CSSProperties = {
  fontSize: 'clamp(1.6rem, 3vw, 2rem)', fontWeight: 800,
  letterSpacing: '-0.03em', color: '#1d1d1f', marginBottom: '1rem',
};
const h3: React.CSSProperties = {
  fontSize: '1.1rem', fontWeight: 700, color: '#1d1d1f', marginBottom: '0.7rem',
};
const prose: React.CSSProperties = {
  fontSize: '1.02rem', color: '#444', lineHeight: 1.78, marginBottom: '0.9rem',
};
const lightbulbBox: React.CSSProperties = {
  background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 14,
  padding: '1.6rem 1.8rem', margin: '1.8rem 0',
};
const problemBadge: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
  background: '#1d1d1f', color: '#fff', fontSize: '0.78rem', fontWeight: 700,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  padding: '0.3rem 0.9rem', borderRadius: 20, marginBottom: '1rem',
};
const th: React.CSSProperties = {
  padding: '0.7rem 0.9rem', textAlign: 'left', fontWeight: 700,
  fontSize: '0.82rem', color: '#555', borderBottom: '2px solid #e5e7eb',
};
const td: React.CSSProperties = {
  padding: '0.7rem 0.9rem', fontSize: '0.84rem', color: '#444',
  borderBottom: '1px solid #f0f0f0', verticalAlign: 'top', lineHeight: 1.55,
};

export default AnchorCaseStudy;
